import React, { useEffect, useState, useRef } from 'react';
import { MapPin, Store, Utensils, CreditCard, Banknote, Smartphone, Loader2, CheckCircle2, Navigation, AlertCircle, Mail, Upload, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Input, Textarea } from '../ui/Input';
import { Button } from '../ui/Button';
import { DeliveryType, Restaurant, RestaurantSettings } from '../../lib/types';
import { cn, validateCaprichoDeliveryZone } from '../../lib/utils';
import { useTableOrder } from '../../lib/table-context';
import { isPreorderRestaurant, getPreorderSlots, getPreorderMaxPerSlot, getPreorderSummary, checkPreorderSlotAvailability, getUpcomingSaturdays, formatDeliveryDate, toDateString, getOrderingWindowMessage } from '../../lib/preorder';
import { useCustomerLookup } from '../../lib/loyalty-hooks';
import { LoyaltyCard } from './LoyaltyCard';
import { useLanguage } from '../../lib/i18n';
import { getCustomerByPhone, getCustomerFromLocal } from '../../lib/customer';
import { supabase } from '../../lib/supabase';
import { uploadPaymentProofToCloudinary } from '../../lib/ticket';
// ─── Crack Pollo dynamic delivery zones ──────────────────────
const CRACK_POLLO_DELIVERY_ZONES: {
  level1: string;
  places: {
    name: string;
    fee: number;
  }[];
}[] = [{
  level1: 'San Pablo',
  places: [{
    name: 'Barrio El Estadio',
    fee: 700
  }, {
    name: 'Barrio Fátima',
    fee: 700
  }, {
    name: 'Barrio Juan Pablo II',
    fee: 700
  }, {
    name: 'Barrio Padre Pío',
    fee: 700
  }, {
    name: 'Bomba El Cruce',
    fee: 700
  }, {
    name: 'Calle Los Moreno',
    fee: 700
  }, {
    name: 'Cementerio San Pablo',
    fee: 700
  }, {
    name: 'La Clara',
    fee: 700
  }, {
    name: 'Los Barriles (Barrio Los Ángeles)',
    fee: 700
  }, {
    name: 'Oficina Volcafé',
    fee: 700
  }, {
    name: 'Rancho San Francisco',
    fee: 700
  }, {
    name: 'Barrio El Rosario',
    fee: 1000
  }, {
    name: 'Cabinas Coyote',
    fee: 1000
  }, {
    name: 'Cafetería Haug',
    fee: 1000
  }, {
    name: 'Coopeleco',
    fee: 1000
  }, {
    name: 'Donguis',
    fee: 1000
  }, {
    name: 'Granja Ecopollo',
    fee: 1000
  }, {
    name: 'Oficinas Ecopollo',
    fee: 1000
  }, {
    name: 'Socolilla',
    fee: 1000
  }, {
    name: 'Abejonal',
    fee: 1500
  }, {
    name: 'Altos de los Gamboa',
    fee: 1500
  }, {
    name: 'Carrizal',
    fee: 1500
  }, {
    name: 'Casquillo',
    fee: 1500
  }, {
    name: 'La Virgen',
    fee: 1500
  }, {
    name: 'Montes de Oro',
    fee: 1500
  }, {
    name: 'San Isidro',
    fee: 2500
  }, {
    name: 'San Antonio',
    fee: 3000
  }]
}, {
  level1: 'San Marcos',
  places: [{
    name: 'Barrio El Estadio San Marcos',
    fee: 1500
  }, {
    name: 'Barrio El Sitio',
    fee: 1500
  }, {
    name: 'Barrio La Clínica',
    fee: 1500
  }, {
    name: 'Barrio Los Navarro',
    fee: 1500
  }, {
    name: 'Calle Los Ulloa',
    fee: 1500
  }, {
    name: 'El Balahar',
    fee: 1500
  }, {
    name: 'La Placa',
    fee: 1500
  }, {
    name: 'La Recta',
    fee: 1500
  }, {
    name: 'Las 3 Marías',
    fee: 1500
  }, {
    name: 'San Marcos Centro',
    fee: 1500
  }, {
    name: 'Santa Cecilia',
    fee: 1500
  }, {
    name: 'Bajo San Juan',
    fee: 2000
  }, {
    name: 'El Rodeo',
    fee: 2000
  }, {
    name: 'La Sabana',
    fee: 2500
  }, {
    name: 'Guadalupe',
    fee: 3000
  }, {
    name: 'Llano de la Piedra',
    fee: 3000
  }, {
    name: 'San Lorenzo',
    fee: 3000
  }, {
    name: 'Canet',
    fee: 4000
  }, {
    name: 'Santa Marta',
    fee: 4000
  }]
}, {
  level1: 'Santa María',
  places: [{
    name: 'Santa María',
    fee: 4000
  }]
}];
function sortCrackPolloPlaces(places: {
  name: string;
  fee: number;
}[]): {
  name: string;
  fee: number;
}[] {
  return [...places].sort((a, b) => {
    if (a.fee !== b.fee) return a.fee - b.fee;
    return a.name.localeCompare(b.name, 'es');
  });
}
function formatCrackPolloFee(fee: number): string {
  return `₡${fee.toLocaleString('es-CR')}`;
}
// Crack Pollo: when the customer picks "Otro" inside a Level 1 zone, the
// express fee is not known yet — the restaurant will confirm it by WhatsApp.
const CRACK_POLLO_PENDING_FEE_LABEL = 'Cobro express pendiente de confirmar por WhatsApp';
interface CheckoutFormProps {
  restaurant: Restaurant | null;
  settings: RestaurantSettings | null;
  onSubmit: (data: CheckoutFormData) => void;
  isLoading: boolean;
  initialCustomer?: {
    customer_name: string;
    customer_phone: string;
    delivery_address: string;
    maps_url: string;
  } | null;
  onCrackPolloDeliveryFeeChange?: (fee: number) => void;
  // MS Express external delivery — emits the currently selected zone/subzone
  // and its fee (or null when nothing is selected) so CheckoutPage can keep
  // the food subtotal separate from the delivery fee. Only used when
  // settings.uses_external_delivery === true.
  onExternalDeliveryChange?: (info: {
    fee: number;
    zone: string;
    subzone: string;
    catalogId: string | null;
    confirmationPending?: boolean;
  } | null) => void;
  orderTotal?: number;
}
// ─── MS Express external delivery catalog row (flexible shape) ──
// The delivery_zones_catalog table already exists in the DB. We read it with
// select('*') and resolve the relevant fields tolerantly so we never break if
// the exact column names differ slightly across environments.
interface MsExpressZoneRow {
  id: string | null;
  zone: string;
  subzone: string;
  fee: number;
}
function resolveMsExpressRow(r: any): MsExpressZoneRow | null {
  const zone = String(r?.zone ?? r?.zone_name ?? r?.provincia ?? '').trim();
  const subzone = String(r?.subzone ?? r?.subzone_name ?? r?.localidad ?? r?.locality ?? '').trim();
  const feeRaw = r?.delivery_fee ?? r?.fee ?? r?.price ?? r?.cost ?? r?.precio ?? 0;
  const fee = Number(feeRaw) || 0;
  if (!zone || !subzone) return null;
  return {
    id: r?.id != null ? String(r.id) : null,
    zone,
    subzone,
    fee
  };
}
export interface CheckoutFormData {
  customer_name: string;
  customer_phone: string;
  customer_email: string;
  delivery_type: DeliveryType;
  payment_method: string;
  delivery_address: string;
  maps_url: string;
  delivery_lat: number | null;
  delivery_lng: number | null;
  table_id: string;
  notes: string;
  preorder_date?: string;
  preorder_time_slot?: string;
  wants_invoice: boolean;
  invoice_customer_name?: string;
  invoice_legal_id?: string;
  invoice_email?: string;
  dynamic_delivery_fee?: number | null;
  delivery_zone_level1?: string;
  delivery_zone_place?: string;
  // MS Express external delivery (only set when uses_external_delivery=true)
  delivery_zone?: string;
  delivery_subzone?: string;
  delivery_zone_catalog_id?: string | null;
  external_delivery_fee?: number;
  // Emilius-only exception: the pizzeria must confirm delivery coverage and
  // cost before the customer receives a final amount or payment instructions.
  delivery_confirmation_pending?: boolean;
  // When the customer pays by SINPE for a delivery and uploads a proof,
  // we upload to Cloudinary and surface the URL here so the WhatsApp
  // message and order confirmation can reference it directly.
  payment_proof_url?: string | null;
}
export function CheckoutForm({
  restaurant,
  settings,
  onSubmit,
  isLoading,
  initialCustomer,
  onCrackPolloDeliveryFeeChange,
  onExternalDeliveryChange,
  orderTotal = 0
}: CheckoutFormProps) {
  const {
    tableOrder,
    isTableOrder
  } = useTableOrder();
  const {
    t
  } = useLanguage();
  // Simplified QR checkout (Name + Email only) is opt-in per restaurant.
  // Currently enabled for NatuCafé (id 19) and Rancho San Francisco (id 28).
  // Other restaurants always use the full marketplace checkout, even if a
  // stale QR table context exists in localStorage from a previous scan.
  const isQrTableForThisRestaurant = isTableOrder && !!tableOrder && !!restaurant?.id && String(tableOrder.restaurantId) === String(restaurant.id);
  const useSimplifiedQrCheckout = isQrTableForThisRestaurant && (Number(restaurant?.id) === 19 || Number(restaurant?.id) === 28);
  const isNatucafeMarketplace = Number(restaurant?.id) === 19 && !useSimplifiedQrCheckout;
  const isCabanaParrillera = Number(restaurant?.id) === 24 || restaurant?.slug === 'cabana-parrillera' || restaurant?.slug === 'la-cabana-parrillera';
  // Emilius Pizza — always offer the three fulfillment channels in checkout
  // (pasar a recoger / comer en el restaurante / express) regardless of the
  // restaurant settings toggles. Strictly additive, Emilius-scoped.
  const isEmilius = Number(restaurant?.id) === 27 || restaurant?.slug === 'emilius-pizza';
  // Emilius Pizza — custom delivery zones
  const [emiliusZones, setEmiliusZones] = useState<{
    parent_zone: string;
    subzone: string;
    price: number;
  }[]>([]);
  const [emiliusZonesLoading, setEmiliusZonesLoading] = useState(false);
  const [selectedEmiliusZone, setSelectedEmiliusZone] = useState<string>('');
  const [selectedEmiliusSubzone, setSelectedEmiliusSubzone] = useState<string>('');
  const [selectedEmiliusFee, setSelectedEmiliusFee] = useState<number>(0);
  // La Cabaña Parrillera — show a one-time popup when the customer picks
  // delivery ("express") so they understand the express fee is quoted
  // separately via WhatsApp and is NOT included in the cart total.
  const [showCabanaExpressNotice, setShowCabanaExpressNotice] = useState(false);
  const cabanaExpressNoticeShownRef = useRef(false);
  // Crack Pollo: dynamic delivery zone selector (3-step picker).
  // Only active for slug 'crack-pollo'. Does NOT affect other restaurants.
  const isCrackPollo = Number(restaurant?.id) === 20 || restaurant?.slug === 'crack-pollo';
  // ¡Qué Galleta! — repostería con entrega programada los sábados. No
  // tiene salón, así que no se ofrece "Comer en el local", y no acepta
  // tarjeta: solo efectivo y SINPE.
  const isQueGalleta = Number(restaurant?.id) === 30 || restaurant?.slug === 'que-galleta';
  const sinpeAccountHolder = isCrackPollo ? 'ANDOMA ADM SRL' : isEmilius ? 'Juan Emilio Ureña Solís' : isQueGalleta ? 'Grupo Jalsna Srl' : restaurant?.name || 'NatuCafé Coffee Shop';
  const [selectedDeliveryLevel1, setSelectedDeliveryLevel1] = useState<string>('');
  const [selectedDeliveryPlace, setSelectedDeliveryPlace] = useState<string>('');
  const [selectedDeliveryFee, setSelectedDeliveryFee] = useState<number>(0);
  const [deliveryZoneConfirmed, setDeliveryZoneConfirmed] = useState<boolean>(false);
  // Crack Pollo: "Otro" option inside each Level 1. Customer types a free-form
  // place name; the express fee stays at 0 and is shown as "pending".
  const [selectedDeliveryPlaceIsOther, setSelectedDeliveryPlaceIsOther] = useState<boolean>(false);
  const [otherPlaceText, setOtherPlaceText] = useState<string>('');
  // ─── MS Express external delivery ──────────────────────────
  // Gated entirely behind restaurant_settings.uses_external_delivery. When
  // the flag is false/null this whole block is inert and the existing flow
  // is unchanged.
  const usesExternalDelivery = settings?.uses_external_delivery === true;
  const [msZones, setMsZones] = useState<MsExpressZoneRow[]>([]);
  const [msZonesLoading, setMsZonesLoading] = useState(false);
  const [selectedMsZone, setSelectedMsZone] = useState<string>('');
  const [selectedMsSubzone, setSelectedMsSubzone] = useState<string>('');
  const [selectedMsFee, setSelectedMsFee] = useState<number>(0);
  const [selectedMsCatalogId, setSelectedMsCatalogId] = useState<string | null>(null);
  // Cabaña Parrillera: customer indicates arrival time when dining in
  const [dineInArrivalTime, setDineInArrivalTime] = useState<string>('');
  const [cashAmount, setCashAmount] = useState<string>('');
  const [formData, setFormData] = useState<CheckoutFormData>({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    delivery_type: useSimplifiedQrCheckout ? 'dine_in' : 'pickup',
    payment_method: 'sinpe',
    delivery_address: '',
    maps_url: '',
    delivery_lat: null,
    delivery_lng: null,
    table_id: useSimplifiedQrCheckout ? tableOrder?.tableId || '' : '',
    notes: '',
    wants_invoice: false,
    invoice_customer_name: '',
    invoice_legal_id: '',
    invoice_email: ''
  });
  const isEmiliusOtherAddress = isEmilius && formData.delivery_type === 'delivery' && selectedEmiliusZone === 'Otra';
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  // Check localStorage for returning customer on mount
  const localCustomer = getCustomerFromLocal();
  const [termsAccepted, setTermsAccepted] = useState(localCustomer?.terms_accepted ?? false);
  const [isReturningCustomer, setIsReturningCustomer] = useState(localCustomer?.terms_accepted ?? false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof CheckoutFormData | 'first_name' | 'last_name', string>>>({});
  const [geoStatus, setGeoStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [geoError, setGeoError] = useState('');
  // Keep saved customer names compatible with the split checkout fields.
  useEffect(() => {
    const nameParts = formData.customer_name.trim().split(/\s+/).filter(Boolean);
    if (nameParts.length < 2 || firstName || lastName) return;
    setFirstName(nameParts[0]);
    setLastName(nameParts.slice(1).join(' '));
  }, [formData.customer_name, firstName, lastName]);
  const updateCustomerName = (nextFirstName: string, nextLastName: string) => {
    setFirstName(nextFirstName);
    setLastName(nextLastName);
    setFormData(prev => ({
      ...prev,
      customer_name: `${nextFirstName.trim()} ${nextLastName.trim()}`.trim()
    }));
    setErrors(prev => ({
      ...prev,
      customer_name: undefined,
      first_name: undefined,
      last_name: undefined
    }));
  };
  // Check if returning customer (already accepted terms) on phone change
  useEffect(() => {
    const checkReturningCustomer = async () => {
      const phone = formData.customer_phone.replace(/\D/g, '');
      if (phone.length < 8) {
        setIsReturningCustomer(false);
        return;
      }
      try {
        const cust = await getCustomerByPhone(phone);
        if (cust && cust.terms_accepted_at) {
          setIsReturningCustomer(true);
          setTermsAccepted(true);
          setFormData(prev => ({
            ...prev,
            customer_name: prev.customer_name || cust.full_name || '',
            customer_email: prev.customer_email || cust.email || '',
            delivery_address: prev.delivery_address || cust.address_text || '',
            maps_url: prev.maps_url || cust.maps_url || ''
          }));
          return;
        } else if (cust) {
          setFormData(prev => ({
            ...prev,
            customer_name: prev.customer_name || cust.full_name || '',
            customer_email: prev.customer_email || cust.email || '',
            delivery_address: prev.delivery_address || cust.address_text || '',
            maps_url: prev.maps_url || cust.maps_url || ''
          }));
          return;
        }
      } catch (err) {
        console.error('[CHECKOUT] checkReturningCustomer Supabase error:', err);
      }
      // Fallback: check localStorage
      const local = getCustomerFromLocal();
      if (local && local.phone === phone) {
        if (local.terms_accepted) {
          setIsReturningCustomer(true);
          setTermsAccepted(true);
        }
        setFormData(prev => ({
          ...prev,
          customer_name: prev.customer_name || local.full_name || '',
          customer_email: prev.customer_email || local.email || '',
          delivery_address: prev.delivery_address || local.address_text || '',
          maps_url: prev.maps_url || local.maps_url || ''
        }));
      }
    };
    const debounce = setTimeout(checkReturningCustomer, 500);
    return () => clearTimeout(debounce);
  }, [formData.customer_phone]);
  // Apply initialCustomer data from localStorage lookup
  useEffect(() => {
    if (initialCustomer) {
      setFormData(prev => ({
        ...prev,
        customer_name: initialCustomer.customer_name || prev.customer_name,
        customer_phone: initialCustomer.customer_phone || prev.customer_phone,
        delivery_address: initialCustomer.delivery_address || prev.delivery_address,
        maps_url: initialCustomer.maps_url || prev.maps_url
      }));
    }
  }, [initialCustomer]);
  const isPreorder = isPreorderRestaurant(restaurant?.id);
  const preorderSaturdays = isPreorder ? getUpcomingSaturdays(3) : [];
  const preorderWindow = isPreorder ? getOrderingWindowMessage(restaurant?.id) : null;
  // Franjas y cupo máximo definidos por restaurante.
  const preorderSlots = isPreorder ? getPreorderSlots(restaurant?.id) : [];
  const preorderMaxPerSlot = getPreorderMaxPerSlot(restaurant?.id);
  const preorderSummary = isPreorder ? getPreorderSummary(restaurant?.id) : '';
  // Cupos ya tomados en cada franja del sábado elegido.
  const [slotAvailability, setSlotAvailability] = useState<Record<string, {
    count: number;
    available: boolean;
  }>>({});
  const [slotsLoading, setSlotsLoading] = useState(false);
  useEffect(() => {
    if (!isPreorder || !restaurant?.id || !formData.preorder_date) {
      setSlotAvailability({});
      return;
    }
    let cancelled = false;
    setSlotsLoading(true);
    checkPreorderSlotAvailability(restaurant.id, formData.preorder_date, preorderSlots, preorderMaxPerSlot).then(res => {
      if (cancelled) return;
      setSlotAvailability(res);
      // Si la franja elegida se llenó mientras el cliente completaba el
      // formulario, se deselecciona para que tenga que elegir otra.
      const chosen = formData.preorder_time_slot;
      if (chosen && res[chosen] && !res[chosen].available) {
        handleChange('preorder_time_slot' as any, '');
      }
    }).finally(() => {
      if (!cancelled) setSlotsLoading(false);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPreorder, restaurant?.id, formData.preorder_date]);
  // Loyalty Integration
  const {
    customer,
    loading: customerLoading
  } = useCustomerLookup(formData.customer_phone, restaurant?.id);
  // Autofill when customer is found
  useEffect(() => {
    if (customer && !formData.customer_name) {
      setFormData(prev => ({
        ...prev,
        customer_name: customer.full_name || prev.customer_name
      }));
    }
  }, [customer]);
  // Autofill address when found
  useEffect(() => {
    if (customer?.address_text && !formData.delivery_address && formData.delivery_type === 'delivery') {
      setFormData(prev => ({
        ...prev,
        delivery_address: customer.address_text || prev.delivery_address,
        maps_url: customer.maps_url || prev.maps_url
      }));
    }
  }, [customer, formData.delivery_type]);
  // Initialize default delivery type based on settings
  useEffect(() => {
    if (settings) {
      if (settings.accept_delivery) setFormData(prev => ({
        ...prev,
        delivery_type: 'delivery'
      }));else if (settings.accept_pickup) setFormData(prev => ({
        ...prev,
        delivery_type: 'pickup'
      }));
    }
  }, [settings]);
  const handleDeliveryTypeChange = (type: DeliveryType) => {
    setFormData(prev => ({
      ...prev,
      delivery_type: type
    }));
  };
  // MS Express: load the external delivery zones once, only when the feature
  // flag is on. Filtered to provider_name='MS Express', restaurant_id IS NULL
  // (shared catalog) and is_active=true.
  useEffect(() => {
    if (!usesExternalDelivery) return;
    let cancelled = false;
    const loadZones = async () => {
      setMsZonesLoading(true);
      try {
        const {
          data,
          error
        } = await supabase.from('delivery_zones_catalog').select('id, zone, subzone, price').eq('provider_name', 'MS Express').is('restaurant_id', null).eq('is_active', true).order('zone', {
          ascending: true
        }).order('price', {
          ascending: true
        }).order('subzone', {
          ascending: true
        });
        if (error) {
          console.error('[MS_EXPRESS] delivery_zones_catalog error:', error);
          if (!cancelled) setMsZones([]);
          return;
        }
        const rows = (data || []).map(resolveMsExpressRow).filter((r): r is MsExpressZoneRow => r !== null);
        if (!cancelled) setMsZones(rows);
      } catch (err) {
        console.error('[MS_EXPRESS] load zones exception:', err);
        if (!cancelled) setMsZones([]);
      } finally {
        if (!cancelled) setMsZonesLoading(false);
      }
    };
    loadZones();
    return () => {
      cancelled = true;
    };
  }, [usesExternalDelivery]);
  // MS Express: reset the selection whenever the customer leaves delivery.
  useEffect(() => {
    if (!usesExternalDelivery) return;
    if (formData.delivery_type !== 'delivery') {
      setSelectedMsZone('');
      setSelectedMsSubzone('');
      setSelectedMsFee(0);
      setSelectedMsCatalogId(null);
    }
  }, [usesExternalDelivery, formData.delivery_type]);
  // MS Express: notify CheckoutPage of the current selection + fee so the
  // order summary (food subtotal / delivery / total) stays in sync.
  useEffect(() => {
    if (!usesExternalDelivery) return;
    if (!onExternalDeliveryChange) return;
    if (formData.delivery_type === 'delivery' && selectedMsZone && selectedMsSubzone) {
      onExternalDeliveryChange({
        fee: selectedMsFee,
        zone: selectedMsZone,
        subzone: selectedMsSubzone,
        catalogId: selectedMsCatalogId
      });
    } else {
      onExternalDeliveryChange(null);
    }
  }, [usesExternalDelivery, formData.delivery_type, selectedMsZone, selectedMsSubzone, selectedMsFee, selectedMsCatalogId, onExternalDeliveryChange]);
  // MS Express: derived helpers for the selector
  const msZoneNames = Array.from(new Set(msZones.map(z => z.zone))).sort((a, b) => a.localeCompare(b, 'es'));
  const msSubzonesForZone = msZones.filter(z => z.zone === selectedMsZone).sort((a, b) => {
    if (a.fee !== b.fee) return a.fee - b.fee;
    return a.subzone.localeCompare(b.subzone, 'es');
  });
  // Emilius Pizza: load custom delivery zones
  useEffect(() => {
    if (!isEmilius) return;
    let cancelled = false;
    const loadEmiliusZones = async () => {
      setEmiliusZonesLoading(true);
      // ─── TEMP DIAGNOSTIC LOGS (Emilius delivery zones) ───
      // Quitar una vez confirmado que las zonas cargan bien.
      console.log('[EMILIUS_ZONES] restaurant.id en checkout =', restaurant?.id, 'typeof =', typeof restaurant?.id);
      console.log('[EMILIUS_ZONES] Ejecutando: SELECT * FROM restaurant_delivery_zone_prices WHERE restaurant_id = 27 ORDER BY parent_zone, subzone');
      try {
        // SELECT * (sin filtro provider_name) — leemos todas las columnas y
        // resolvemos parent_zone/subzone/price de forma tolerante por si el
        // nombre exacto de alguna columna difiere.
        const {
          data,
          error,
          status
        } = await supabase.from('restaurant_delivery_zone_prices').select('*').eq('restaurant_id', 27).order('parent_zone', {
          ascending: true
        }).order('subzone', {
          ascending: true
        });
        console.log('[EMILIUS_ZONES] status =', status, '· error =', error, '· filas devueltas =', (data || []).length);
        if ((data || []).length > 0) {
          console.log('[EMILIUS_ZONES] primera fila cruda =', data![0]);
        }
        if (error) {
          console.error('[EMILIUS_ZONES] delivery zones error:', error);
          if (!cancelled) setEmiliusZones([]);
          return;
        }
        const mapped = (data || []).map((r: any) => ({
          parent_zone: String(r?.parent_zone ?? r?.parentZone ?? r?.zone ?? '').trim(),
          subzone: String(r?.subzone ?? r?.sub_zone ?? r?.subZone ?? '').trim(),
          price: Number(r?.price ?? r?.fee ?? r?.delivery_fee ?? r?.precio ?? 0) || 0
        })).filter(r => r.parent_zone && r.subzone);
        const parentCount = new Set(mapped.map(r => r.parent_zone)).size;
        console.log('[EMILIUS_ZONES] filas válidas mapeadas =', mapped.length, '· parent_zone únicas =', parentCount);
        if (!cancelled) setEmiliusZones(mapped);
      } catch (err) {
        console.error('[EMILIUS_ZONES] load zones exception:', err);
        if (!cancelled) setEmiliusZones([]);
      } finally {
        if (!cancelled) setEmiliusZonesLoading(false);
      }
    };
    loadEmiliusZones();
    return () => {
      cancelled = true;
    };
  }, [isEmilius, restaurant?.id]);
  // Emilius Pizza: reset selection when leaving delivery
  useEffect(() => {
    if (!isEmilius) return;
    if (formData.delivery_type !== 'delivery') {
      setSelectedEmiliusZone('');
      setSelectedEmiliusSubzone('');
      setSelectedEmiliusFee(0);
    }
  }, [isEmilius, formData.delivery_type]);
  // Emilius Pizza: la tarjeta no existe en Delivery. Si el cliente la
  // eligió en Llevar y luego cambia a Delivery, se limpia la selección
  // para que no quede un método oculto pero activo.
  useEffect(() => {
    if (!isEmilius) return;
    if (formData.delivery_type === 'delivery' && formData.payment_method === 'card') {
      setFormData(prev => ({
        ...prev,
        payment_method: ''
      }));
    }
  }, [isEmilius, formData.delivery_type, formData.payment_method]);
  // Emilius Pizza: notify CheckoutPage of the current selection
  useEffect(() => {
    if (!isEmilius) return;
    if (!onExternalDeliveryChange) return;
    if (formData.delivery_type === 'delivery' && selectedEmiliusZone === 'Otra') {
      onExternalDeliveryChange({
        fee: 0,
        zone: 'Otra',
        subzone: '',
        catalogId: null,
        confirmationPending: true
      });
    } else if (formData.delivery_type === 'delivery' && selectedEmiliusZone && selectedEmiliusSubzone) {
      onExternalDeliveryChange({
        fee: selectedEmiliusFee,
        zone: selectedEmiliusZone,
        subzone: selectedEmiliusSubzone,
        catalogId: null
      });
    } else {
      onExternalDeliveryChange(null);
    }
  }, [isEmilius, formData.delivery_type, selectedEmiliusZone, selectedEmiliusSubzone, selectedEmiliusFee, onExternalDeliveryChange]);
  // Emilius Pizza: derived helpers
  // La zona "OTROS" del catálogo no se ofrece al cliente: para
  // direcciones fuera de cobertura ya existe la opción explícita
  // "Otra — No encuentro mi dirección" al final del selector.
  const emiliusZoneNames = Array.from(new Set(emiliusZones.map(z => z.parent_zone))).filter(zone => zone.trim().toUpperCase() !== 'OTROS').sort((a, b) => a.localeCompare(b, 'es'));
  const emiliusSubzonesForZone = emiliusZones.filter(z => z.parent_zone === selectedEmiliusZone).sort((a, b) => {
    if (a.price !== b.price) return a.price - b.price;
    return a.subzone.localeCompare(b.subzone, 'es');
  });
  // La Cabaña Parrillera — fire the express-fee notice once per checkout
  // session, the first time the customer selects delivery.
  useEffect(() => {
    if (!isCabanaParrillera) return;
    if (formData.delivery_type !== 'delivery') return;
    if (cabanaExpressNoticeShownRef.current) return;
    cabanaExpressNoticeShownRef.current = true;
    setShowCabanaExpressNotice(true);
  }, [isCabanaParrillera, formData.delivery_type]);
  const [sinpeProofFile, setSinpeProofFile] = useState<File | null>(null);
  const [sinpeProofPreview, setSinpeProofPreview] = useState<string>('');
  const [uploadingProof, setUploadingProof] = useState(false);
  // NatuCafé marketplace: el método de pago ya NO se fuerza a SINPE.
  // El cliente puede elegir entre SINPE, Efectivo o Tarjeta (estos dos
  // últimos se pagan contra entrega con el mensajero). El default inicial
  // sigue siendo 'sinpe' (ver estado inicial de formData).
  // Crack Pollo: reset zone state whenever the customer switches away from delivery.
  // This guarantees the express fee returns to 0 for pickup/dine_in.
  useEffect(() => {
    if (!isCrackPollo) return;
    if (formData.delivery_type !== 'delivery') {
      setSelectedDeliveryLevel1('');
      setSelectedDeliveryPlace('');
      setSelectedDeliveryFee(0);
      setDeliveryZoneConfirmed(false);
      setSelectedDeliveryPlaceIsOther(false);
      setOtherPlaceText('');
    }
  }, [isCrackPollo, formData.delivery_type]);
  // Crack Pollo: if the customer switches to delivery while having card selected,
  // clear the payment method since card is not allowed for express delivery.
  useEffect(() => {
    if (!isCrackPollo) return;
    if (formData.delivery_type === 'delivery' && formData.payment_method === 'card') {
      setFormData(prev => ({
        ...prev,
        payment_method: 'cash'
      }));
    }
  }, [isCrackPollo, formData.delivery_type, formData.payment_method]);
  // Crack Pollo: notify parent (CheckoutPage) of the current effective fee
  // so the order summary sidebar and totals stay in sync in real time.
  useEffect(() => {
    if (!isCrackPollo) return;
    if (!onCrackPolloDeliveryFeeChange) return;
    const effective = formData.delivery_type === 'delivery' && deliveryZoneConfirmed ? selectedDeliveryFee : 0;
    onCrackPolloDeliveryFeeChange(effective);
  }, [isCrackPollo, formData.delivery_type, deliveryZoneConfirmed, selectedDeliveryFee, onCrackPolloDeliveryFeeChange]);
  const validate = () => {
    const newErrors: Partial<Record<keyof CheckoutFormData | 'first_name' | 'last_name', string>> = {};
    if (!firstName.trim()) {
      newErrors.first_name = 'El nombre es requerido';
    }
    if (!lastName.trim()) {
      newErrors.last_name = 'El primer apellido es requerido';
    }
    // Phone is optional only in the simplified NatuCafé QR flow.
    // Every other checkout (marketplace, other restaurants) requires it.
    if (!useSimplifiedQrCheckout) {
      if (!formData.customer_phone.trim()) newErrors.customer_phone = 'El teléfono es requerido';else if (formData.customer_phone.replace(/\D/g, '').length < 8) {
        newErrors.customer_phone = 'Ingresa un número válido de 8 dígitos';
      }
    } else if (formData.customer_phone.trim() && formData.customer_phone.replace(/\D/g, '').length < 8) {
      newErrors.customer_phone = 'Ingresa un número válido de 8 dígitos';
    }
    if (formData.delivery_type === 'delivery') {
      if (!formData.delivery_address.trim()) newErrors.delivery_address = 'La dirección es requerida para delivery';
      // GPS is mandatory for delivery
      if (formData.delivery_lat === null || formData.delivery_lng === null) {
        ;
        (newErrors as any).maps_url = 'La ubicación GPS es obligatoria para delivery';
      }
      // Crack Pollo: zone must be confirmed before continuing.
      if (isCrackPollo && !deliveryZoneConfirmed) {
        ;
        (newErrors as any).delivery_zone = 'Selecciona y confirma tu zona de entrega';
      }
      // MS Express: zone + subzone are mandatory before continuing.
      if (usesExternalDelivery) {
        if (!selectedMsZone) {
          ;
          (newErrors as any).ms_zone = 'Selecciona tu zona de entrega';
        } else if (!selectedMsSubzone) {
          ;
          (newErrors as any).ms_subzone = 'Selecciona tu localidad';
        }
      }
      // Emilius Pizza: "Otra" is a valid exception; delivery coverage and
      // cost are confirmed after the WhatsApp handoff. Regular zones keep the
      // existing zone + subzone requirement.
      if (isEmilius) {
        if (!selectedEmiliusZone) {
          ;
          (newErrors as any).emilius_zone = 'Selecciona tu zona';
        } else if (selectedEmiliusZone !== 'Otra' && !selectedEmiliusSubzone) {
          ;
          (newErrors as any).emilius_subzone = 'Selecciona tu ubicación';
        }
      }
      // Capricho de Canela: validate delivery zone (5km radius)
      const isCapricho = String(restaurant?.id) === '2';
      if (isCapricho && formData.delivery_lat !== null && formData.delivery_lng !== null) {
        const zoneCheck = validateCaprichoDeliveryZone(formData.delivery_lat, formData.delivery_lng);
        if (!zoneCheck.valid) {
          newErrors.delivery_address = `Lo sentimos, solo hacemos delivery en San Marcos de Tarrazú, Santa María de Dota y San Pablo de León Cortés (máximo 5km del centro). Tu ubicación está a ${zoneCheck.distance?.toFixed(1)}km de ${zoneCheck.nearestZone}.`;
        }
      }
    }
    if (formData.delivery_type === 'dine_in' && !useSimplifiedQrCheckout) {
      // Marketplace dine-in (any restaurant): require arrival time so the
      // kitchen has the order ready when the customer arrives.
      if (!dineInArrivalTime.trim()) (newErrors as any).dine_in_arrival_time = 'Indica a qué hora llegarás';
    }
    if (formData.wants_invoice) {
      if (!formData.invoice_customer_name?.trim()) (newErrors as any).invoice_customer_name = 'El nombre o razón social es requerido';
      if (!formData.invoice_email?.trim()) (newErrors as any).invoice_email = 'El correo es requerido para enviar la factura';else if (!/^\S+@\S+\.\S+$/.test(formData.invoice_email)) {
        ;
        (newErrors as any).invoice_email = 'Ingresa un correo válido';
      }
    }
    if (isPreorder) {
      if (!formData.preorder_date) (newErrors as any).preorder_date = 'Selecciona una fecha de entrega';
      if (!formData.preorder_time_slot) (newErrors as any).preorder_time_slot = 'Selecciona un horario de entrega';
    }
    // SINPE proof required for any restaurant when payment method is SINPE
    // (NatuCafé marketplace, Cabaña Parrillera, etc).
    const requiresSinpeProof = !useSimplifiedQrCheckout && formData.payment_method === 'sinpe' && !isEmiliusOtherAddress;
    if (requiresSinpeProof && !sinpeProofFile) {
      ;
      (newErrors as any).sinpe_proof = 'Debes adjuntar la captura del pago SINPE para continuar';
    }
    setErrors(newErrors);
    // Scroll to first error field
    if (Object.keys(newErrors).length > 0) {
      const firstErrorKey = Object.keys(newErrors)[0];
      setTimeout(() => {
        const el = document.querySelector(`[data-field="${firstErrorKey}"]`);
        if (el) {
          el.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      }, 50);
    }
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    let finalFormData = {
      ...formData
    };
    // Crack Pollo: inject delivery zone info into notes and dynamic fee field
    // so CheckoutPage uses the per-place fee and the WhatsApp / kitchen ticket
    // both show the exact zone the customer selected.
    if (isCrackPollo && formData.delivery_type === 'delivery' && deliveryZoneConfirmed && selectedDeliveryLevel1 && selectedDeliveryPlace) {
      const zoneLines = [`📍 Zona principal: ${selectedDeliveryLevel1}`, `📍 Lugar: ${selectedDeliveryPlace}`, `🚚 Tarifa express: ${selectedDeliveryPlaceIsOther ? CRACK_POLLO_PENDING_FEE_LABEL : formatCrackPolloFee(selectedDeliveryFee)}`].join('\n');
      finalFormData = {
        ...finalFormData,
        notes: finalFormData.notes ? `${zoneLines}\n${finalFormData.notes}` : zoneLines,
        dynamic_delivery_fee: selectedDeliveryFee,
        delivery_zone_level1: selectedDeliveryLevel1,
        delivery_zone_place: selectedDeliveryPlace
      };
    }
    // MS Express: attach the chosen zone/subzone/fee so CheckoutPage and the
    // create-order payload keep the delivery fee separate from the food
    // subtotal. Only runs behind the uses_external_delivery flag.
    if (usesExternalDelivery && formData.delivery_type === 'delivery' && selectedMsZone && selectedMsSubzone) {
      finalFormData = {
        ...finalFormData,
        delivery_zone: selectedMsZone,
        delivery_subzone: selectedMsSubzone,
        delivery_zone_catalog_id: selectedMsCatalogId,
        external_delivery_fee: selectedMsFee
      };
    }
    // Emilius Pizza: attach regular zone pricing, or mark "Otra" as pending
    // confirmation. The pending case intentionally has no payment or final fee.
    if (isEmilius && formData.delivery_type === 'delivery' && selectedEmiliusZone === 'Otra') {
      const pendingLine = '⚠️ DELIVERY Y COSTO PENDIENTES DE CONFIRMACIÓN POR LA PIZZERÍA. El cliente debe ser contactado por WhatsApp antes de finalizar el pedido.';
      finalFormData = {
        ...finalFormData,
        payment_method: 'pending_confirmation',
        delivery_zone: 'Otra',
        delivery_subzone: '',
        external_delivery_fee: 0,
        delivery_confirmation_pending: true,
        notes: finalFormData.notes ? `${pendingLine}\n${finalFormData.notes}` : pendingLine
      };
    } else if (isEmilius && formData.delivery_type === 'delivery' && selectedEmiliusZone && selectedEmiliusSubzone) {
      finalFormData = {
        ...finalFormData,
        delivery_zone: selectedEmiliusZone,
        delivery_subzone: selectedEmiliusSubzone,
        external_delivery_fee: selectedEmiliusFee
      };
    }
    // Crack Pollo & Cabaña Parrillera (marketplace): inject cash amount and
    // change into notes so the kitchen knows how much to give back.
    if ((isCrackPollo || isCabanaParrillera && !isQrTableForThisRestaurant) && formData.payment_method === 'cash' && cashAmount) {
      const cashVal = Number(cashAmount);
      const changeVal = cashVal >= orderTotal ? cashVal - orderTotal : 0;
      const cashLines = `💵 Paga con: ₡${cashVal.toLocaleString('es-CR')} — Vuelto: ₡${changeVal.toLocaleString('es-CR')}`;
      finalFormData = {
        ...finalFormData,
        notes: finalFormData.notes ? `${cashLines}\n${finalFormData.notes}` : cashLines
      };
    }
    // Marketplace dine-in (any restaurant): prepend arrival time to notes so
    // it appears on the kitchen ticket / order detail clearly.
    if (!useSimplifiedQrCheckout && formData.delivery_type === 'dine_in' && dineInArrivalTime.trim()) {
      const arrivalLine = `🕐 Hora de llegada: ${dineInArrivalTime.trim()}`;
      finalFormData = {
        ...finalFormData,
        notes: finalFormData.notes ? `${arrivalLine}\n${finalFormData.notes}` : arrivalLine
      };
    }
    // SINPE proof upload — for any restaurant accepting SINPE.
    // NEW: when this is a Delivery + SINPE order we upload to Cloudinary
    // so the WhatsApp message can attach the public secure_url directly
    // (replacing the tracking link). For every other SINPE flow (pickup,
    // dine-in, NatuCafé marketplace) we keep the existing Supabase
    // Storage upload exactly as it was to avoid any regression.
    const shouldUploadProof = !useSimplifiedQrCheckout && formData.payment_method === 'sinpe' && !isEmiliusOtherAddress;
    if (shouldUploadProof && sinpeProofFile) {
      const isDeliverySinpe = formData.payment_method === 'sinpe' && formData.delivery_type === 'delivery';
      if (isDeliverySinpe) {
        try {
          setUploadingProof(true);
          const cloudUrl = await uploadPaymentProofToCloudinary(sinpeProofFile, restaurant?.slug || `restaurant-${restaurant?.id ?? 'unknown'}`, `${restaurant?.slug || 'order'}-${Date.now()}-sinpe`);
          if (!cloudUrl) {
            setErrors(prev => ({
              ...(prev as any),
              sinpe_proof: 'No pudimos subir el comprobante. Intentá nuevamente.'
            }));
            setUploadingProof(false);
            return;
          }
          // NOTE: We intentionally do NOT inject the proof URL into notes
          // here. The WhatsApp message renders a dedicated "COMPROBANTE DE
          // PAGO" block from payment_proof_url, so duplicating it inside
          // notes would show the link twice in the WhatsApp output.
          finalFormData = {
            ...finalFormData,
            payment_proof_url: cloudUrl
          };
          setUploadingProof(false);
        } catch (err: any) {
          setErrors(prev => ({
            ...(prev as any),
            sinpe_proof: 'No pudimos subir el comprobante. Intentá nuevamente.'
          }));
          setUploadingProof(false);
          return;
        }
      } else {
        try {
          setUploadingProof(true);
          const ext = sinpeProofFile.name.split('.').pop() || 'jpg';
          const safeName = (formData.customer_name || 'cliente').toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') || 'cliente';
          const restaurantFolder = `restaurant-${restaurant?.id ?? 'unknown'}`;
          const path = `sinpe-proofs/${restaurantFolder}/${Date.now()}-${safeName}.${ext}`;
          const {
            error: uploadError
          } = await supabase.storage.from('menu-images').upload(path, sinpeProofFile, {
            upsert: true,
            contentType: sinpeProofFile.type
          });
          if (uploadError) {
            setErrors(prev => ({
              ...(prev as any),
              sinpe_proof: 'No pudimos subir tu captura. Verificá tu conexión e intentá de nuevo.'
            }));
            setUploadingProof(false);
            return;
          }
          const {
            data: urlData
          } = supabase.storage.from('menu-images').getPublicUrl(path);
          const proofUrl = urlData.publicUrl;
          const proofLine = `Comprobante SINPE: ${proofUrl}`;
          finalFormData = {
            ...finalFormData,
            notes: finalFormData.notes ? `${finalFormData.notes}\n${proofLine}` : proofLine
          };
          setUploadingProof(false);
        } catch (err: any) {
          setErrors(prev => ({
            ...(prev as any),
            sinpe_proof: 'No pudimos subir tu captura. Verificá tu conexión e intentá de nuevo.'
          }));
          setUploadingProof(false);
          return;
        }
      }
    }
    onSubmit(finalFormData);
  };
  const handleChange = (field: keyof CheckoutFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };
  const handleGeolocation = () => {
    if (!navigator.geolocation) {
      setGeoStatus('error');
      setGeoError('Tu navegador no soporta geolocalización');
      return;
    }
    setGeoStatus('loading');
    setGeoError('');
    navigator.geolocation.getCurrentPosition(position => {
      const {
        latitude,
        longitude
      } = position.coords;
      // Capricho de Canela: validate delivery zone immediately
      const isCapricho = String(restaurant?.id) === '2';
      if (isCapricho) {
        const zoneCheck = validateCaprichoDeliveryZone(latitude, longitude);
        if (!zoneCheck.valid) {
          setGeoStatus('error');
          setGeoError(`Tu ubicación está fuera de nuestra zona de delivery. Solo entregamos en San Marcos de Tarrazú, Santa María de Dota y San Pablo de León Cortés (máximo 5km del centro). Estás a ${zoneCheck.distance?.toFixed(1)}km de ${zoneCheck.nearestZone}.`);
          return;
        }
      }
      setFormData(prev => ({
        ...prev,
        delivery_lat: latitude,
        delivery_lng: longitude,
        maps_url: `https://maps.google.com/?q=${latitude},${longitude}`
      }));
      setGeoStatus('success');
    }, error => {
      setGeoStatus('error');
      switch (error.code) {
        case error.PERMISSION_DENIED:
          setGeoError('Permiso denegado. Por favor, permite el acceso a tu ubicación.');
          break;
        case error.POSITION_UNAVAILABLE:
          setGeoError('La información de ubicación no está disponible.');
          break;
        case error.TIMEOUT:
          setGeoError('Se agotó el tiempo de espera para obtener la ubicación.');
          break;
        default:
          setGeoError('Ocurrió un error desconocido al obtener la ubicación.');
          break;
      }
    }, {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    });
  };
  // Determine available payment methods from restaurant
  const showSinpe = restaurant?.accepts_sinpe ?? true;
  const showCash = restaurant?.accepts_cash ?? true;
  const showCard = restaurant?.accepts_card ?? true;
  return <form onSubmit={handleSubmit} className="space-y-8" data-id="element-1824" data-mp="1sr36jjglox4p">
      {/* QR Table Banner — only for the simplified NatuCafé QR flow */}
      {useSimplifiedQrCheckout && tableOrder && <section className="bg-emerald-50 p-5 rounded-2xl border border-emerald-200 flex items-center gap-4" data-id="element-1825" data-mp="1gblkqkla7jc6">
          <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center shrink-0" data-id="element-1826" data-mp="1bdturw4767am">
            <Utensils className="w-6 h-6 text-emerald-600" data-id="element-1827" data-mp="16f0exx1fu47a" />
          </div>
          <div data-id="element-1828" data-mp="12cdjlm5k615c">
            <p className="font-bold text-emerald-900 text-lg" data-id="element-1829" data-mp="12cvplai0vtqg">
              {tableOrder.tableName} · Autoatención
            </p>
            <p className="text-sm text-emerald-700" data-id="element-1830" data-mp="skpye2zlhgcb">
              Tu pedido se enviará directamente a esta mesa
            </p>
          </div>
        </section>}

      {/* Contact Info */}
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-id="element-1831" data-mp="imtn9b6nqle7">
        <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2" data-id="element-1832" data-mp="elgil4fawo8v">
          <Store className="w-6 h-6 text-brand-primary" data-id="element-1833" data-mp="enubp7qf7qds" />
          {t('checkout_title')}
        </h2>

        <div className="space-y-4" data-id="element-1834" data-mp="22gboc3h70bvo">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1835" data-mp="27hiuxz7ln50r">
            <div data-field="first_name" data-id="element-1836" data-mp="1sk09b5q7x7xj">
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5" data-id="element-1837" data-mp="1siymk3zuawzz">
                Nombre <span className="text-red-500" data-id="element-1838" data-mp="1xhs365afb44d">*</span>
                {errors.first_name && <AlertCircle className="w-4 h-4 text-red-500" data-id="element-1839" data-mp="1jqwoudh5nute" />}
              </label>
              <Input value={firstName} onChange={e => updateCustomerName(e.target.value, lastName)} error={errors.first_name} autoComplete="given-name" required data-id="element-1840" data-mp="1ojy62s4u46g5" />
            </div>
            <div data-field="last_name" data-id="element-1841" data-mp="10skfaqmc0ixa">
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5" data-id="element-1842" data-mp="10ux3hlionpis">
                Primer apellido <span className="text-red-500" data-id="element-1843" data-mp="m63tujzxvh3o">*</span>
                {errors.last_name && <AlertCircle className="w-4 h-4 text-red-500" data-id="element-1844" data-mp="h70muu0ef0t2" />}
              </label>
              <Input value={lastName} onChange={e => updateCustomerName(firstName, e.target.value)} error={errors.last_name} autoComplete="family-name" required data-id="element-1845" data-mp="v6s7vepc1hgk" />
            </div>
          </div>

          {!useSimplifiedQrCheckout && <div data-field="customer_phone" data-id="element-1846" data-mp="104tqxzmrdgs1">
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5" data-id="element-1847" data-mp="104253giyqxey">
                {t('checkout_phone')}
                {errors.customer_phone && <AlertCircle className="w-4 h-4 text-red-500" data-id="element-1848" data-mp="19xsq2bfvsppw" />}
              </label>
              <div className="relative" data-id="element-1849" data-mp="1ntd00hdmtxl6">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium" data-id="element-1850" data-mp="1ix5psce7dzxf">
                  📞
                </span>
                <Input type="tel" placeholder="88888888" value={formData.customer_phone} onChange={e => setFormData({
              ...formData,
              customer_phone: e.target.value
            })} error={errors.customer_phone} required data-id="element-1851" data-mp="1bco3460ju2iz" />
              </div>
            </div>}

          <div data-field="customer_email" data-id="element-1852" data-mp="nmbpceimhbkh">
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5" data-id="element-1853" data-mp="jmb1625q1m08">
              {t('checkout_email')}
              {errors.customer_email && <AlertCircle className="w-4 h-4 text-red-500" data-id="element-1854" data-mp="eldjdqomitz9" />}
            </label>
            <Input value={formData.customer_email} onChange={e => handleChange('customer_email', e.target.value)} error={errors.customer_email} placeholder="email@ejemplo.com" data-id="element-1855" data-mp="9py7h4a6f85h" />
          </div>

          {!useSimplifiedQrCheckout && <LoyaltyCard phone={formData.customer_phone} restaurantId={restaurant?.id} data-id="element-1856" data-mp="4j9v826pf1lp" />}
        </div>
      </div>

      {/* Preorder Schedule — for preorder restaurants */}
      {isPreorder && preorderWindow && <section className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm" data-id="element-1857" data-mp="381jjgd7i5zrt">
          <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-1858" data-mp="3czutanxssaa1">
            📦 Entrega Programada
          </h3>

          {/* Schedule Info */}
          <div className={cn('rounded-xl p-4 border mb-5', preorderWindow.isOpen ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200')} data-id="element-1859" data-mp="3mowb8jnwl1qn">
            <p className={cn('font-bold text-sm mb-1', preorderWindow.isOpen ? 'text-emerald-900' : 'text-red-900')} data-id="element-1860" data-mp="2u7ag0eul0drc">
              {preorderWindow.title}
            </p>
            <p className={cn('text-xs', preorderWindow.isOpen ? 'text-emerald-700' : 'text-red-700')} data-id="element-1861" data-mp="2vxpcraji1gsx">
              {preorderWindow.message}
            </p>
            <p className="mt-2 text-xs text-gray-500" data-id="element-1862" data-mp="2388gg1srdfrf">{preorderSummary}</p>
          </div>

          {/* Date Selection */}
          <div className="mb-5" data-id="element-1863" data-mp="1ybhzlc94qg4y">
            <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-1864" data-mp="1ycb569wcqzli">
              Fecha de entrega (sábado)
            </label>
            <div className="space-y-2" data-id="element-1865" data-mp="287uzwsz6js8o">
              {preorderSaturdays.map(sat => {
            const dateStr = toDateString(sat);
            const isSelected = formData.preorder_date === dateStr;
            return <button key={dateStr} type="button" onClick={() => handleChange('preorder_date' as any, dateStr)} className={cn('w-full p-3.5 rounded-xl border-2 text-sm font-semibold transition-all text-left', isSelected ? 'border-brand-amber bg-amber-50/50 text-gray-900' : 'border-gray-100 hover:border-gray-200 text-gray-600')} data-id="element-1866" data-mp="3jn098fttgp19">
                    📅 {formatDeliveryDate(sat)}
                  </button>;
          })}
            </div>
            {(errors as any).preorder_date && <p className="text-sm text-red-500 mt-1" data-id="element-1867" data-mp="904kx5zar6kf">
                {(errors as any).preorder_date}
              </p>}
          </div>

          {/* Time Slot Selection */}
          {formData.preorder_date && <div data-id="element-1868" data-mp="11is1q979fzfp">
              <div className="flex items-baseline justify-between mb-3" data-id="element-1869" data-mp="11jbktt8nctvb">
                <label className="block text-sm font-medium text-gray-700" data-id="element-1870" data-mp="39zuq148g0bqm">
                  Horario de entrega
                </label>
                {slotsLoading && <span className="text-xs text-gray-400" data-id="element-1871" data-mp="301evsse4cmcw">
                    Revisando cupos…
                  </span>}
              </div>
              <div className="grid grid-cols-2 gap-2" data-id="element-1872" data-mp="3sp1bgz0mfhg4">
                {preorderSlots.map(slot => {
            const isSelected = formData.preorder_time_slot === slot.id;
            const info = slotAvailability[slot.id];
            // Mientras se consultan los cupos, la franja se muestra
            // habilitada: nunca se bloquea por falta de datos.
            const isFull = info ? !info.available : false;
            const left = info ? Math.max(preorderMaxPerSlot - info.count, 0) : null;
            return <button key={slot.id} type="button" disabled={isFull} aria-disabled={isFull} onClick={() => handleChange('preorder_time_slot' as any, slot.id)} className={cn('p-3 rounded-xl border-2 text-xs font-semibold transition-all text-center', isFull ? 'border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed' : isSelected ? 'border-brand-amber bg-amber-50/50 text-gray-900' : 'border-gray-100 hover:border-gray-200 text-gray-600')} data-id="element-1873" data-mp="1t6fs6vm83wg6">
                      <span className="block" data-id="element-1874" data-mp="2uwkxgkni37np">🕐 {slot.label}</span>
                      {isFull ? <span className="block mt-1 text-[11px] font-medium text-gray-400" data-id="element-1875" data-mp="2zvnzrxc3izrz">
                          Sin cupos
                        </span> : left !== null && left <= 2 ? <span className="block mt-1 text-[11px] font-medium text-amber-600" data-id="element-1876" data-mp="uuv6kh1ybzkd">
                          {left === 1 ? 'Último cupo' : `Quedan ${left}`}
                        </span> : null}
                    </button>;
          })}
              </div>
              {(errors as any).preorder_time_slot && <p className="text-sm text-red-500 mt-1" data-id="element-1877" data-mp="1ei14c96yrkz7">
                  {(errors as any).preorder_time_slot}
                </p>}
            </div>}
        </section>}

      {/* Delivery Type — hidden only for the simplified NatuCafé QR flow */}
      {!useSimplifiedQrCheckout && <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-id="element-1878" data-mp="3u96vohdfdmxf">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-1879" data-mp="3pa3yfj3osl00">
            <MapPin className="w-5 h-5 text-brand-primary" data-id="element-1880" data-mp="24qqkv8lnuadn" />
            {t('checkout_delivery_type')}
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-1881" data-mp="29pblva8qky35">
            {(settings?.accept_delivery || isEmilius) && <button type="button" onClick={() => setFormData(prev => ({
          ...prev,
          delivery_type: 'delivery'
        }))} className={cn('relative flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all', formData.delivery_type === 'delivery' ? 'border-brand-primary bg-brand-primary/10 text-brand-primary ring-2 ring-brand-primary/20' : 'border-gray-100 hover:border-brand-primary/30 text-gray-600')} data-id="element-1882" data-mp="1v16znwah64fm">
                {formData.delivery_type === 'delivery' && <CheckCircle2 className="absolute top-2 right-2 w-5 h-5 text-brand-primary" data-id="element-1883" data-mp="1z63mvv5lj2yg" />}
                <Navigation className="w-6 h-6 mb-2" data-id="element-1884" data-mp="1u6qterk7yxmw" />
                <span className="font-semibold" data-id="element-1885" data-mp="1u9d7zb5j2dem">{t('delivery')}</span>
              </button>}

            {(settings?.accept_pickup || isEmilius) && <button type="button" onClick={() => setFormData(prev => ({
          ...prev,
          delivery_type: 'pickup'
        }))} className={cn('relative flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all', formData.delivery_type === 'pickup' ? 'border-brand-primary bg-brand-primary/10 text-brand-primary ring-2 ring-brand-primary/20' : 'border-gray-100 hover:border-brand-primary/30 text-gray-600')} data-id="element-1886" data-mp="1kgo00q96rho1">
                {formData.delivery_type === 'pickup' && <CheckCircle2 className="absolute top-2 right-2 w-5 h-5 text-brand-primary" data-id="element-1887" data-mp="33e2ghld7c6cn" />}
                <Store className="w-6 h-6 mb-2" data-id="element-1888" data-mp="38cvkmn6lml05" />
                <span className="font-semibold" data-id="element-1889" data-mp="3d6zrl6fv6t18">{t('pickup')}</span>
              </button>}

            {/* Comer en el local — disponible salvo en negocios sin salón
                (¡Qué Galleta! entrega programada, no tiene local para
                consumir). */}
            {!isQueGalleta && <button type="button" onClick={() => setFormData(prev => ({
          ...prev,
          delivery_type: 'dine_in'
        }))} className={cn('relative flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all', formData.delivery_type === 'dine_in' ? 'border-brand-primary bg-brand-primary/10 text-brand-primary ring-2 ring-brand-primary/20' : 'border-gray-100 hover:border-brand-primary/30 text-gray-600')} data-id="element-1890" data-mp="2pgw01xfyajql">
              {formData.delivery_type === 'dine_in' && <CheckCircle2 className="absolute top-2 right-2 w-5 h-5 text-brand-primary" data-id="element-1891" data-mp="1ysz5sgggp50q" />}
              <Utensils className="w-6 h-6 mb-2" data-id="element-1892" data-mp="22t991lcvdhbw" />
              <span className="font-semibold" data-id="element-1893" data-mp="164bcpyph1bcm">Comer en el local</span>
            </button>}
          </div>

          {/* Conditional Fields based on Delivery Type */}
          <div className="mt-6 space-y-4" data-id="element-1894" data-mp="1f4gari8ryxe4">
            {formData.delivery_type === 'delivery' && <div className="mt-6 space-y-4" data-id="element-1895" data-mp="1k3jd2uxdepie">
                {/* Crack Pollo: 3-step zone selector — only shown for slug 'crack-pollo' */}
                {isCrackPollo && <div data-field="delivery_zone" className="rounded-2xl border-2 border-brand-amber/40 bg-amber-50/40 p-4 sm:p-5" data-id="element-1896" data-mp="2qlpvs97jxxhj">
                    {/* Step 1: pick Level 1 */}
                    {!selectedDeliveryLevel1 && !deliveryZoneConfirmed && <>
                        <h4 className="text-base sm:text-lg font-bold text-gray-900" data-id="element-1897" data-mp="2vw1y20h9g8ba">
                          ¿Para dónde va tu pedido?
                        </h4>
                        <p className="text-sm text-gray-600 mt-1 mb-4" data-id="element-1898" data-mp="2mw5icocumjsd">
                          Primero elegí la zona principal.
                        </p>
                        <div className="space-y-2.5" data-id="element-1899" data-mp="2hvhgt8qzukjw">
                          {CRACK_POLLO_DELIVERY_ZONES.map(z => <button key={z.level1} type="button" onClick={() => {
                  setSelectedDeliveryLevel1(z.level1);
                  setSelectedDeliveryPlace('');
                  setSelectedDeliveryFee(0);
                  setDeliveryZoneConfirmed(false);
                  setSelectedDeliveryPlaceIsOther(false);
                  setOtherPlaceText('');
                  if ((errors as any).delivery_zone) {
                    setErrors(prev => ({
                      ...(prev as any),
                      delivery_zone: undefined
                    }));
                  }
                }} className="w-full flex items-center justify-between gap-3 px-4 py-4 rounded-xl border-2 border-gray-200 bg-white text-left hover:border-brand-amber hover:bg-amber-50 transition-colors" data-id="element-1900" data-mp="2d026gqs736s8">
                              <span className="font-bold text-gray-900 text-base" data-id="element-1901" data-mp="yy4p56l9im5m">
                                {z.level1}
                              </span>
                              <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" data-id="element-1902" data-mp="13v4lgpsb8ajj" />
                            </button>)}
                        </div>
                        {(errors as any).delivery_zone && <p className="text-sm text-red-500 mt-3 flex items-center gap-1.5" data-id="element-1903" data-mp="15dlcuszksn4x">
                            <AlertCircle className="w-3.5 h-3.5 shrink-0" data-id="element-1904" data-mp="10fa3skh3ugnr" />
                            {(errors as any).delivery_zone}
                          </p>}
                      </>}

                    {/* Step 2: pick Level 2 place within selected Level 1 */}
                    {selectedDeliveryLevel1 && !deliveryZoneConfirmed && <>
                        <div className="flex items-center justify-between gap-3 mb-3" data-id="element-1905" data-mp="hoaa55yo0io5">
                          <button type="button" onClick={() => {
                  setSelectedDeliveryLevel1('');
                  setSelectedDeliveryPlace('');
                  setSelectedDeliveryFee(0);
                  setSelectedDeliveryPlaceIsOther(false);
                  setOtherPlaceText('');
                }} className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-600 hover:text-gray-900" data-id="element-1906" data-mp="3q3tybqkhbawc">
                            <ChevronLeft className="w-4 h-4" data-id="element-1907" data-mp="3743m4f5p90tf" />
                            Volver
                          </button>
                          <span className="text-xs font-bold text-brand-amber uppercase tracking-wider" data-id="element-1908" data-mp="3c62pvq5cpzqw">
                            {selectedDeliveryLevel1}
                          </span>
                        </div>
                        <h4 className="text-base sm:text-lg font-bold text-gray-900" data-id="element-1909" data-mp="1mp1qgxc5bh8y">
                          Elegí tu lugar exacto
                        </h4>
                        <p className="text-sm text-gray-600 mt-1 mb-4" data-id="element-1910" data-mp="1qqx0cq8572yc">
                          Las tarifas se ordenan de menor a mayor.
                        </p>
                        <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1" data-id="element-1911" data-mp="1eg5scfl35frh">
                          {sortCrackPolloPlaces(CRACK_POLLO_DELIVERY_ZONES.find(z => z.level1 === selectedDeliveryLevel1)?.places || []).map(p => {
                  const isSelected = !selectedDeliveryPlaceIsOther && selectedDeliveryPlace === p.name;
                  return <button key={p.name} type="button" onClick={() => {
                    setSelectedDeliveryPlace(p.name);
                    setSelectedDeliveryFee(p.fee);
                    setSelectedDeliveryPlaceIsOther(false);
                    setOtherPlaceText('');
                  }} className={cn('w-full flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl border-2 text-left transition-colors', isSelected ? 'border-brand-amber bg-amber-50 ring-2 ring-brand-amber/20' : 'border-gray-200 bg-white hover:border-brand-amber/60 hover:bg-amber-50/50')} data-id="element-1912" data-mp="1o6h58rmqyzai">
                                <div className="min-w-0" data-id="element-1913" data-mp="3mo0silj6bszt">
                                  <div className="font-bold text-gray-900 text-sm sm:text-base truncate" data-id="element-1914" data-mp="3moka214wwfdb">
                                    {p.name}
                                  </div>
                                  <div className="text-xs sm:text-sm text-gray-600 mt-0.5" data-id="element-1915" data-mp="3dm1b3s48ymwc">
                                    Express:{' '}
                                    <span className="font-bold text-brand-amber" data-id="element-1916" data-mp="2u3dqqcnvl9ua">
                                      {formatCrackPolloFee(p.fee)}
                                    </span>
                                  </div>
                                </div>
                                {isSelected && <CheckCircle2 className="w-5 h-5 text-brand-amber shrink-0" data-id="element-1917" data-mp="3d0564b0ar4t2" />}
                              </button>;
                })}

                          {/* "Otro:" option — customer types free-form place */}
                          <button type="button" onClick={() => {
                  setSelectedDeliveryPlaceIsOther(true);
                  setSelectedDeliveryPlace('');
                  setSelectedDeliveryFee(0);
                }} className={cn('w-full flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl border-2 border-dashed text-left transition-colors', selectedDeliveryPlaceIsOther ? 'border-brand-amber bg-amber-50 ring-2 ring-brand-amber/20' : 'border-gray-300 bg-white hover:border-brand-amber/60 hover:bg-amber-50/50')} data-id="element-1918" data-mp="3hqlo88f2belr">
                            <div className="min-w-0" data-id="element-1919" data-mp="n7w24tckltsh">
                              <div className="font-bold text-gray-900 text-sm sm:text-base" data-id="element-1920" data-mp="s8tl385emrv1">
                                Otro:
                              </div>
                              <div className="text-xs sm:text-sm text-gray-600 mt-0.5" data-id="element-1921" data-mp="3vps55i8uig9i">
                                Mi lugar no está en la lista
                              </div>
                            </div>
                            {selectedDeliveryPlaceIsOther && <CheckCircle2 className="w-5 h-5 text-brand-amber shrink-0" data-id="element-1922" data-mp="3t2zvzhktcmdw" />}
                          </button>
                        </div>

                        {/* Free-form "Otro" input — rendered OUTSIDE the
                            scroll container so it's always visible when
                            the customer taps the Otro card. */}
                        {selectedDeliveryPlaceIsOther && <div className="mt-3 rounded-xl border-2 border-brand-amber bg-white p-4 space-y-2" data-id="element-1923" data-mp="35eqcimb2oapx">
                            <label className="block text-sm font-bold text-gray-900" data-id="element-1924" data-mp="30h82stp8drgv">
                              ¿Dónde va el pedido?{' '}
                              <span className="text-red-500" data-id="element-1925" data-mp="2rg0ft7s60d23">*</span>
                            </label>
                            <input type="text" autoFocus value={otherPlaceText} onChange={e => setOtherPlaceText(e.target.value)} placeholder="Ej: Frente a la pulpería..." className="w-full h-12 px-3 rounded-lg border-2 border-gray-200 text-base focus:outline-none focus:border-brand-amber bg-white" data-id="element-1926" data-mp="2mh5otik1ni6w" />
                            <p className="text-xs font-bold text-brand-amber" data-id="element-1927" data-mp="1cmihm4m5g8ui">
                              {CRACK_POLLO_PENDING_FEE_LABEL}
                            </p>
                            <p className="text-xs text-gray-600" data-id="element-1928" data-mp="1mfz931ytdbv0">
                              💡 Acordate de completar también la{' '}
                              <span className="font-semibold" data-id="element-1929" data-mp="ty53pd0r7wo7">
                                Dirección exacta
                              </span>{' '}
                              y la{' '}
                              <span className="font-semibold" data-id="element-1930" data-mp="ox7l4zbu10m6">
                                Ubicación GPS
                              </span>{' '}
                              abajo.
                            </p>
                          </div>}
                        <button type="button" disabled={selectedDeliveryPlaceIsOther ? !otherPlaceText.trim() : !selectedDeliveryPlace} onClick={() => {
                if (selectedDeliveryPlaceIsOther) {
                  const txt = otherPlaceText.trim();
                  if (!txt) return;
                  setSelectedDeliveryPlace(`Otro: ${txt}`);
                  setSelectedDeliveryFee(0);
                } else if (!selectedDeliveryPlace) {
                  return;
                }
                setDeliveryZoneConfirmed(true);
                if ((errors as any).delivery_zone) {
                  setErrors(prev => ({
                    ...(prev as any),
                    delivery_zone: undefined
                  }));
                }
              }} className={cn('mt-4 w-full h-12 rounded-xl font-bold text-base transition-colors', (selectedDeliveryPlaceIsOther ? otherPlaceText.trim() : selectedDeliveryPlace) ? 'bg-brand-amber text-white hover:bg-brand-amber-hover' : 'bg-gray-200 text-gray-400 cursor-not-allowed')} data-id="element-1931" data-mp="2qq10vzhq7ep0">
                          Confirmar ubicación
                        </button>
                      </>}

                    {/* Step 3: confirmed */}
                    {deliveryZoneConfirmed && <div className="rounded-xl bg-white border-2 border-emerald-200 p-4" data-id="element-1932" data-mp="1ngizgbutpysz">
                        <div className="flex items-center gap-2 mb-3" data-id="element-1933" data-mp="1seuaur0rohdb">
                          <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" data-id="element-1934" data-mp="volar4jxb3mx" />
                          <span className="font-bold text-emerald-700" data-id="element-1935" data-mp="vqo7e7fkzv0m">
                            Tu entrega
                          </span>
                        </div>
                        <dl className="space-y-1.5 text-sm" data-id="element-1936" data-mp="14notg7f6hh0w">
                          <div className="flex justify-between gap-3" data-id="element-1937" data-mp="19octtjpara7s">
                            <dt className="text-gray-500" data-id="element-1938" data-mp="2g45c09m14drz">Zona principal</dt>
                            <dd className="font-semibold text-gray-900 text-right" data-id="element-1939" data-mp="2g69fwrjxhcs9">
                              {selectedDeliveryLevel1}
                            </dd>
                          </div>
                          <div className="flex justify-between gap-3" data-id="element-1940" data-mp="3u21di8f7chl9">
                            <dt className="text-gray-500" data-id="element-1941" data-mp="3p2ogiuum9u4y">Lugar</dt>
                            <dd className="font-semibold text-gray-900 text-right" data-id="element-1942" data-mp="6vqvka88kg7b">
                              {selectedDeliveryPlace}
                            </dd>
                          </div>
                          <div className="flex justify-between gap-3" data-id="element-1943" data-mp="gsv7lh8davae">
                            <dt className="text-gray-500" data-id="element-1944" data-mp="bulfxgluqctj">Express</dt>
                            <dd className="font-bold text-brand-amber text-right" data-id="element-1945" data-mp="bshauvc7v7ro">
                              {selectedDeliveryPlaceIsOther ? CRACK_POLLO_PENDING_FEE_LABEL : formatCrackPolloFee(selectedDeliveryFee)}
                            </dd>
                          </div>
                        </dl>
                        <button type="button" onClick={() => {
                setDeliveryZoneConfirmed(false);
              }} className="mt-3 w-full h-10 rounded-lg border-2 border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-1946" data-mp="ume3gvi5j5tv">
                          Cambiar zona
                        </button>
                      </div>}
                  </div>}

                {/* Emilius Pizza custom delivery selector */}
                {isEmilius && <div data-field="emilius_zone" className="rounded-2xl border-2 border-brand-amber/40 bg-amber-50/40 p-4 sm:p-5 space-y-4" data-id="element-1947" data-mp="lifua9unypcn">
                    <div data-id="element-1948" data-mp="chrbl1ubs0tf">
                      <h4 className="text-base sm:text-lg font-bold text-gray-900 flex items-center gap-2" data-id="element-1949" data-mp="cikdnpica258">
                        <Navigation className="w-5 h-5 text-brand-amber" data-id="element-1950" data-mp="ght86wl7e41u" />
                        Express Emilius
                      </h4>
                      <p className="text-sm text-gray-600 mt-1" data-id="element-1951" data-mp="2j7kw89lc1bls">
                        Elegí tu zona y ubicación para calcular el costo de
                        envío.
                      </p>
                    </div>

                    {emiliusZonesLoading ? <div className="flex items-center gap-2 text-sm text-gray-500" data-id="element-1952" data-mp="24iou0t5yist3">
                        <Loader2 className="w-4 h-4 animate-spin" data-id="element-1953" data-mp="323azs18wyg1s" />
                        Cargando zonas de entrega...
                      </div> : emiliusZones.length === 0 ? <p className="text-sm text-red-600 flex items-center gap-1.5" data-id="element-1954" data-mp="2s4va82pjbeue">
                        <AlertCircle className="w-4 h-4 shrink-0" data-id="element-1955" data-mp="2o3tc5p7ct8u0" />
                        No hay zonas de entrega disponibles en este momento.
                      </p> : <>
                        {/* Zone */}
                        <div data-id="element-1956" data-mp="3od1a7cq572n7">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1957" data-mp="3oe2p5saa49a7">
                            Selecciona tu zona{' '}
                            <span className="text-red-500" data-id="element-1958" data-mp="3scjzslsws1wd">*</span>
                          </label>
                          <select value={selectedEmiliusZone} onChange={e => {
                  setSelectedEmiliusZone(e.target.value);
                  setSelectedEmiliusSubzone('');
                  setSelectedEmiliusFee(0);
                  setErrors(prev => ({
                    ...(prev as any),
                    emilius_zone: undefined,
                    emilius_subzone: undefined
                  }));
                }} className={cn('w-full h-12 px-3 rounded-xl border-2 bg-white text-gray-900 font-medium focus:outline-none transition-all', (errors as any).emilius_zone ? 'border-red-300 focus:border-red-400' : 'border-gray-200 focus:border-brand-amber')} data-id="element-1959" data-mp="168rq5phe0vj">
                            <option value="" data-id="element-1960" data-mp="1196ujb2m7mzp">Selecciona tu zona</option>
                            {emiliusZoneNames.map(z => <option key={z} value={z} data-id="element-1961" data-mp="diulfwvmvk7j">
                                {z}
                              </option>)}
                            <option value="Otra" data-id="element-1962" data-mp="nhs8evdev5of">
                              Otra — No encuentro mi dirección
                            </option>
                          </select>
                          {(errors as any).emilius_zone && <p className="text-sm text-red-500 mt-1 flex items-center gap-1.5" data-id="element-1963" data-mp="1yxqo3i33gpym">
                              <AlertCircle className="w-3.5 h-3.5 shrink-0" data-id="element-1964" data-mp="1tyxc5u3gh70k" />
                              {(errors as any).emilius_zone}
                            </p>}
                        </div>

                        {/* Pending-confirmation guidance for "Otra" */}
                        {isEmiliusOtherAddress && <div role="status" className="rounded-2xl border-2 border-amber-400 bg-amber-50 p-5" data-id="element-1965" data-mp="37tvvloxfyk1e">
                            <p className="text-base sm:text-lg font-black leading-relaxed text-amber-950" data-id="element-1966" data-mp="3dkyu2j73dll">
                              LA PIZZERÍA TIENE QUE CONFIRMAR EL DELIVERY A TU
                              UBICACIÓN, POR FAVOR CONTINUA EL PROCESO Y ENVIAR
                              EL MENSAJE DE WHATSAPP, LUEGO ESPERA A SER
                              CONTACTADO POR LA PIZZERÍA PARA FINALIZAR TU
                              PEDIDO.
                            </p>
                            <p className="mt-3 text-sm font-semibold text-amber-800" data-id="element-1967" data-mp="2ira3pfegrceb">
                              No se solicitará pago ni se mostrará un precio
                              final hasta confirmar la cobertura y el costo del
                              delivery.
                            </p>
                          </div>}

                        {/* Subzone */}
                        {selectedEmiliusZone && selectedEmiliusZone !== 'Otra' && <div data-field="emilius_subzone" data-id="element-1968" data-mp="36g1rlul0t8la">
                              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1969" data-mp="2shva3p0dpl7s">
                                Selecciona tu ubicación{' '}
                                <span className="text-red-500" data-id="element-1970" data-mp="2whdmtxi1qpzj">*</span>
                              </label>
                              <select value={selectedEmiliusSubzone} onChange={e => {
                  const sub = e.target.value;
                  setSelectedEmiliusSubzone(sub);
                  const match = emiliusSubzonesForZone.find(s => s.subzone === sub);
                  setSelectedEmiliusFee(match?.price ?? 0);
                  setErrors(prev => ({
                    ...(prev as any),
                    emilius_subzone: undefined
                  }));
                }} className={cn('w-full h-12 px-3 rounded-xl border-2 bg-white text-gray-900 font-medium focus:outline-none transition-all', (errors as any).emilius_subzone ? 'border-red-300 focus:border-red-400' : 'border-gray-200 focus:border-brand-amber')} data-id="element-1971" data-mp="1h4j69e4md2lm">
                                <option value="" data-id="element-1972" data-mp="10t4c610hiezq">
                                  Selecciona tu ubicación
                                </option>
                                {emiliusSubzonesForZone.map(s => <option key={s.subzone} value={s.subzone} data-id="element-1973" data-mp="1ajh7mo9u7skn">
                                    {s.subzone} — ₡
                                    {s.price.toLocaleString('es-CR')}
                                  </option>)}
                              </select>
                              {(errors as any).emilius_subzone && <p className="text-sm text-red-500 mt-1 flex items-center gap-1.5" data-id="element-1974" data-mp="ruizunllvw7e">
                                  <AlertCircle className="w-3.5 h-3.5 shrink-0" data-id="element-1975" data-mp="1y8ry9cxh46l2" />
                                  {(errors as any).emilius_subzone}
                                </p>}
                            </div>}

                        {/* Selected fee summary */}
                        {selectedEmiliusZone && selectedEmiliusZone !== 'Otra' && selectedEmiliusSubzone && <div className="rounded-xl bg-white border-2 border-emerald-200 p-4 flex items-center justify-between" data-id="element-1976" data-mp="390rffoiuhxii">
                              <div className="flex items-center gap-2 min-w-0" data-id="element-1977" data-mp="34y4gl3nrbcbt">
                                <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" data-id="element-1978" data-mp="2zzjfz34leqmu" />
                                <span className="text-sm text-gray-700 truncate" data-id="element-1979" data-mp="2v2t1gk8fs33j">
                                  {selectedEmiliusZone} ·{' '}
                                  {selectedEmiliusSubzone}
                                </span>
                              </div>
                              <span className="font-bold text-brand-amber shrink-0" data-id="element-1980" data-mp="obbrr2exaox3">
                                ₡{selectedEmiliusFee.toLocaleString('es-CR')}
                              </span>
                            </div>}
                      </>}
                  </div>}

                {/* MS Express external delivery selector — only when the
                    restaurant uses external delivery and delivery is chosen */}
                {usesExternalDelivery && <div data-field="ms_zone" className="rounded-2xl border-2 border-brand-amber/40 bg-amber-50/40 p-4 sm:p-5 space-y-4" data-id="element-1981" data-mp="djoxhqvbcp2h">
                    <div data-id="element-1982" data-mp="3pf200b2ranj">
                      <h4 className="text-base sm:text-lg font-bold text-gray-900 flex items-center gap-2" data-id="element-1983" data-mp="8moszke43udh">
                        <Navigation className="w-5 h-5 text-brand-amber" data-id="element-1984" data-mp="3qsjyqucj8ub1" />
                        Delivery MS Express
                      </h4>
                      <p className="text-sm text-gray-600 mt-1" data-id="element-1985" data-mp="3utdl3jabu11t">
                        Elegí tu zona y localidad para calcular el costo de
                        envío.
                      </p>
                    </div>

                    {msZonesLoading ? <div className="flex items-center gap-2 text-sm text-gray-500" data-id="element-1986" data-mp="3l6svgomh9kco">
                        <Loader2 className="w-4 h-4 animate-spin" data-id="element-1987" data-mp="3g7g5vw481z6e" />
                        Cargando zonas de entrega...
                      </div> : msZones.length === 0 ? <p className="text-sm text-red-600 flex items-center gap-1.5" data-id="element-1988" data-mp="3q5m5u86gwv8m">
                        <AlertCircle className="w-4 h-4 shrink-0" data-id="element-1989" data-mp="7rdaaoqdw0yf" />
                        No hay zonas de entrega disponibles en este momento.
                      </p> : <>
                        {/* Zone */}
                        <div data-id="element-1990" data-mp="hq2h9mgqs0zw">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1991" data-mp="hn7wtuxqag6h">
                            Zona <span className="text-red-500" data-id="element-1992" data-mp="comvfs6qpqgg">*</span>
                          </label>
                          <select value={selectedMsZone} onChange={e => {
                  setSelectedMsZone(e.target.value);
                  setSelectedMsSubzone('');
                  setSelectedMsFee(0);
                  setSelectedMsCatalogId(null);
                  setErrors(prev => ({
                    ...(prev as any),
                    ms_zone: undefined,
                    ms_subzone: undefined
                  }));
                }} className={cn('w-full h-12 px-3 rounded-xl border-2 bg-white text-gray-900 font-medium focus:outline-none transition-all', (errors as any).ms_zone ? 'border-red-300 focus:border-red-400' : 'border-gray-200 focus:border-brand-amber')} data-id="element-1993" data-mp="36gmmkewb4f05">
                            <option value="" data-id="element-1994" data-mp="1wj5080xg9hdz">Selecciona una zona</option>
                            {msZoneNames.map(z => <option key={z} value={z} data-id="element-1995" data-mp="1mma4fpsk9v3e">
                                {z}
                              </option>)}
                          </select>
                          {(errors as any).ms_zone && <p className="text-sm text-red-500 mt-1 flex items-center gap-1.5" data-id="element-1996" data-mp="2fbgzufwpy4kf">
                              <AlertCircle className="w-3.5 h-3.5 shrink-0" data-id="element-1997" data-mp="2aajmcgmnlyp9" />
                              {(errors as any).ms_zone}
                            </p>}
                        </div>

                        {/* Subzone */}
                        {selectedMsZone && <div data-field="ms_subzone" data-id="element-1998" data-mp="utj5v8nenuaz">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1999" data-mp="2tzcf4c87x401">
                              Localidad <span className="text-red-500" data-id="element-2000" data-mp="2yz75meo2zrkk">*</span>
                            </label>
                            <select value={selectedMsSubzone} onChange={e => {
                  const sub = e.target.value;
                  setSelectedMsSubzone(sub);
                  const match = msSubzonesForZone.find(s => s.subzone === sub);
                  setSelectedMsFee(match?.fee ?? 0);
                  setSelectedMsCatalogId(match?.id ?? null);
                  setErrors(prev => ({
                    ...(prev as any),
                    ms_subzone: undefined
                  }));
                }} className={cn('w-full h-12 px-3 rounded-xl border-2 bg-white text-gray-900 font-medium focus:outline-none transition-all', (errors as any).ms_subzone ? 'border-red-300 focus:border-red-400' : 'border-gray-200 focus:border-brand-amber')} data-id="element-2001" data-mp="2kzxs5mr9fbka">
                              <option value="" data-id="element-2002" data-mp="3rv6kl3sgs2l4">Selecciona tu localidad</option>
                              {msSubzonesForZone.map(s => <option key={s.subzone} value={s.subzone} data-id="element-2003" data-mp="5n6pv8ejiu79">
                                  {s.subzone} — ₡{s.fee.toLocaleString('es-CR')}
                                </option>)}
                            </select>
                            {(errors as any).ms_subzone && <p className="text-sm text-red-500 mt-1 flex items-center gap-1.5" data-id="element-2004" data-mp="1c1pmg8llwi1p">
                                <AlertCircle className="w-3.5 h-3.5 shrink-0" data-id="element-2005" data-mp="314h23sgil7r0" />
                                {(errors as any).ms_subzone}
                              </p>}
                          </div>}

                        {/* Selected fee summary */}
                        {selectedMsZone && selectedMsSubzone && <div className="rounded-xl bg-white border-2 border-emerald-200 p-4 flex items-center justify-between" data-id="element-2006" data-mp="3js3z39cvuw3n">
                            <div className="flex items-center gap-2 min-w-0" data-id="element-2007" data-mp="3ftwtnanvdh7r">
                              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" data-id="element-2008" data-mp="3azit6tucojw8" />
                              <span className="text-sm text-gray-700 truncate" data-id="element-2009" data-mp="3617laonm8jgn">
                                {selectedMsZone} · {selectedMsSubzone}
                              </span>
                            </div>
                            <span className="font-bold text-brand-amber shrink-0" data-id="element-2010" data-mp="2jz1w0d8pbjt">
                              ₡{selectedMsFee.toLocaleString('es-CR')}
                            </span>
                          </div>}
                      </>}
                  </div>}

                <div data-field="delivery_address" data-id="element-2011" data-mp="nvy1ka8b51bs">
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5" data-id="element-2012" data-mp="nxr8lqmu1nkb">
                    {t('checkout_address')}
                    {errors.delivery_address && <AlertCircle className="w-4 h-4 text-red-500" data-id="element-2013" data-mp="xsj7qyw2cih7" />}
                  </label>
                  <Textarea label="Dirección exacta" placeholder="Provincia, Cantón, Distrito, señas exactas..." value={formData.delivery_address} onChange={e => handleChange('delivery_address', e.target.value)} error={errors.delivery_address} required data-id="element-2014" data-mp="12n6vgvj8t8uq" />
                </div>

                <div className="space-y-2" data-field="maps_url" data-id="element-2015" data-mp="38ng4ryt60yfr">
                  <label className="block text-sm font-medium text-gray-700 flex items-center gap-1.5" data-id="element-2016" data-mp="3mdjk05tmnrvp">
                    Ubicación GPS (Obligatorio)
                    {(errors as any).maps_url && <AlertCircle className="w-4 h-4 text-red-500" data-id="element-2017" data-mp="63e8b3ru85d" />}
                  </label>

                  <button type="button" onClick={handleGeolocation} disabled={geoStatus === 'loading'} className={cn('w-full h-12 flex items-center justify-center rounded-xl border-2 border-dashed font-medium transition-all disabled:opacity-70', geoStatus === 'idle' && !(errors as any).maps_url && 'border-gray-200 text-gray-600 hover:border-brand-amber hover:text-brand-amber bg-gray-50', geoStatus === 'idle' && (errors as any).maps_url && 'border-red-300 text-red-600 bg-red-50 animate-pulse', geoStatus === 'loading' && 'border-brand-amber text-brand-amber bg-amber-50/50', geoStatus === 'success' && 'border-emerald-300 text-emerald-700 bg-emerald-50', geoStatus === 'error' && 'border-red-300 text-red-600 bg-red-50')} data-id="element-2018" data-mp="3rburwb0d3sba">
                    {geoStatus === 'idle' && <>
                        <Navigation className="w-5 h-5 mr-2" data-id="element-2019" data-mp="3txubuolhbfci" />
                        Usar mi ubicación actual
                      </>}
                    {geoStatus === 'loading' && <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" data-id="element-2020" data-mp="13fbav50yzcbn" />
                        Obteniendo ubicación...
                      </>}
                    {geoStatus === 'success' && <>
                        <CheckCircle2 className="w-5 h-5 mr-2" data-id="element-2021" data-mp="tfu8awh0lejw" />
                        Ubicación capturada
                      </>}
                    {geoStatus === 'error' && <>
                        <AlertCircle className="w-5 h-5 mr-2" data-id="element-2022" data-mp="kijzdhro61i5" />
                        Reintentar ubicación
                      </>}
                  </button>

                  {(errors as any).maps_url && geoStatus === 'idle' && <p className="text-sm text-red-500 flex items-center gap-1.5 px-1" data-id="element-2023" data-mp="5sm4kiz8idjz">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0" data-id="element-2024" data-mp="spzshipqi3d" />
                      {(errors as any).maps_url}
                    </p>}

                  {geoStatus === 'success' && <div className="flex items-center justify-between px-1" data-id="element-2025" data-mp="28d6n3ifap9t8">
                      <a href={formData.maps_url} target="_blank" rel="noreferrer" className="text-xs text-emerald-600 hover:underline flex items-center" data-id="element-2026" data-mp="2cfaa94bbu2uc">
                        📍 Ver en el mapa
                      </a>
                      <button type="button" onClick={handleGeolocation} className="text-xs text-gray-500 hover:text-gray-900 underline" data-id="element-2027" data-mp="1orttmyxfbwj8">
                        Actualizar
                      </button>
                    </div>}

                  {geoStatus === 'error' && <p className="text-xs text-red-500 px-1" data-id="element-2028" data-mp="15yy6mnpqjsrd">{geoError}</p>}
                </div>
              </div>}

            {formData.delivery_type === 'dine_in' && <div data-field="dine_in_arrival_time" className="space-y-2" data-id="element-2029" data-mp="12wlgf4j1xdhp">
                <label className="block text-sm font-medium text-gray-700 flex items-center gap-1.5" data-id="element-2030" data-mp="yvi06gx3dffy">
                  ¿A qué hora llegarás al local?
                  <span className="text-red-500" data-id="element-2031" data-mp="1cpsfy8r41z9v">*</span>
                  {(errors as any).dine_in_arrival_time && <AlertCircle className="w-4 h-4 text-red-500" data-id="element-2032" data-mp="jz0mhj1fjoq8" />}
                </label>
                <input type="time" value={dineInArrivalTime} onChange={e => {
            setDineInArrivalTime(e.target.value);
            if ((errors as any).dine_in_arrival_time) {
              setErrors(prev => ({
                ...(prev as any),
                dine_in_arrival_time: undefined
              }));
            }
          }} className={cn('w-full h-12 px-4 rounded-xl border-2 bg-white text-gray-900 font-medium focus:outline-none focus:ring-2 focus:ring-brand-primary/20 transition-all', (errors as any).dine_in_arrival_time ? 'border-red-300 focus:border-red-400' : 'border-gray-200 focus:border-brand-primary')} required data-id="element-2033" data-mp="fy6yyqrwgbxv" />
                <p className="text-xs text-gray-500 px-1" data-id="element-2034" data-mp="5v1o20jlq8mr">
                  Tendremos tu pedido listo para esa hora.
                </p>
                {(errors as any).dine_in_arrival_time && <p className="text-sm text-red-500 px-1 flex items-center gap-1.5" data-id="element-2035" data-mp="3s8jtbyb4fbe5">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0" data-id="element-2036" data-mp="1207zp7ns31l" />
                    {(errors as any).dine_in_arrival_time}
                  </p>}
              </div>}
          </div>
        </div>}

      {/* Payment is intentionally deferred only for Emilius "Otra" delivery. */}
      {isEmiliusOtherAddress && <div className="rounded-2xl border-2 border-amber-400 bg-amber-50 p-6" data-id="element-2037" data-mp="2poaknrzjswpw">
          <h3 className="text-lg font-black text-amber-950" data-id="element-2038" data-mp="2kug3av7f0ttz">
            Pago pendiente de confirmación
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-amber-900" data-id="element-2039" data-mp="2fubhqqrnh8ww">
            Continuá para registrar tu solicitud y enviá el mensaje de WhatsApp.
            La pizzería confirmará si llega a tu ubicación y el costo antes de
            solicitar cualquier pago.
          </p>
        </div>}

      {/* Payment Method — hidden only for the simplified NatuCafé QR flow */}
      {!useSimplifiedQrCheckout && !isEmiliusOtherAddress && <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-id="element-2040" data-mp="2ukiucpuz7xpo">
          {isNatucafeMarketplace ? <>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-2041" data-mp="1n5w7sindlgsf">
                <CreditCard className="w-5 h-5 text-brand-primary" data-id="element-2042" data-mp="1j8i0iwj99j32" />
                Método de pago
              </h3>

              {/* NatuCafé: selector SINPE / Efectivo / Tarjeta */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-2043" data-mp="2qhq2ocxsxjx">
                <button type="button" onClick={() => setFormData(prev => ({
            ...prev,
            payment_method: 'sinpe'
          }))} className={cn('relative flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all', formData.payment_method === 'sinpe' ? 'border-brand-primary bg-brand-primary/10 text-brand-primary ring-2 ring-brand-primary/20' : 'border-gray-100 hover:border-brand-primary/30 text-gray-600')} data-id="element-2044" data-mp="7nzymdn1lare">
                  {formData.payment_method === 'sinpe' && <CheckCircle2 className="absolute top-2 right-2 w-5 h-5 text-brand-primary" data-id="element-2045" data-mp="323yomcsr6roe" />}
                  <Smartphone className="w-6 h-6 mb-2" data-id="element-2046" data-mp="2y2v95remaxnp" />
                  <span className="font-semibold" data-id="element-2047" data-mp="2y46evwrmifhg">SINPE Móvil</span>
                </button>

                <button type="button" onClick={() => setFormData(prev => ({
            ...prev,
            payment_method: 'cash'
          }))} className={cn('relative flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all', formData.payment_method === 'cash' ? 'border-brand-primary bg-brand-primary/10 text-brand-primary ring-2 ring-brand-primary/20' : 'border-gray-100 hover:border-brand-primary/30 text-gray-600')} data-id="element-2048" data-mp="3c3frynkj8th7">
                  {formData.payment_method === 'cash' && <CheckCircle2 className="absolute top-2 right-2 w-5 h-5 text-brand-primary" data-id="element-2049" data-mp="n7uxdxrb1e9k" />}
                  <Banknote className="w-6 h-6 mb-2" data-id="element-2050" data-mp="r8evb3jpclwr" />
                  <span className="font-semibold" data-id="element-2051" data-mp="d9xb3ft09rjl">Efectivo</span>
                </button>

                <button type="button" onClick={() => setFormData(prev => ({
            ...prev,
            payment_method: 'card'
          }))} className={cn('relative flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all', formData.payment_method === 'card' ? 'border-brand-primary bg-brand-primary/10 text-brand-primary ring-2 ring-brand-primary/20' : 'border-gray-100 hover:border-brand-primary/30 text-gray-600')} data-id="element-2052" data-mp="dcb7pt3c3bt9">
                  {formData.payment_method === 'card' && <CheckCircle2 className="absolute top-2 right-2 w-5 h-5 text-brand-primary" data-id="element-2053" data-mp="32lwz5zqfsh7w" />}
                  <CreditCard className="w-6 h-6 mb-2" data-id="element-2054" data-mp="2xt3ybmy39t13" />
                  <span className="font-semibold" data-id="element-2055" data-mp="2suixpmexd7c4">Tarjeta</span>
                </button>
              </div>

              {/* Efectivo / Tarjeta = contra entrega con el mensajero.
                                      Solo aplica para DELIVERY (con mensajero). En Llevar /
                                      Comer en el local no hay mensajero, así que no se muestra. */}
              {formData.delivery_type === 'delivery' && (formData.payment_method === 'cash' || formData.payment_method === 'card') && <div className="mt-5 rounded-2xl border-2 border-amber-200 bg-amber-50 p-4 flex items-start gap-3" data-id="element-2056" data-mp="36otf1ioldz85">
                    <Banknote className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" data-id="element-2057" data-mp="1v7jv6v1jx56b" />
                    <p className="text-sm text-amber-800 leading-relaxed" data-id="element-2058" data-mp="1v9oasa8tbsmf">
                      <span className="font-bold" data-id="element-2059" data-mp="ar418syd1tei">
                        {formData.payment_method === 'cash' ? 'Efectivo' : 'Tarjeta'}{' '}
                        contra entrega.
                      </span>{' '}
                      Pagás directamente al mensajero al recibir tu pedido. Si
                      pagás en efectivo, tené listo el monto exacto.
                    </p>
                  </div>}

              {/* SINPE: número + comprobante (solo cuando se elige SINPE) */}
              {formData.payment_method === 'sinpe' && <div className="mt-5" data-field="sinpe_proof" data-id="element-2060" data-mp="13c3vbw1508gi">
                  <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-5 mb-5" data-id="element-2061" data-mp="18c8ejttfjhaf">
                    <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-1" data-id="element-2062" data-mp="356la9z3946b5">
                      Realizá el SINPE Móvil a este número
                    </p>
                    <p className="text-3xl sm:text-4xl font-black text-emerald-900 tracking-tight tabular-nums mb-1" data-id="element-2063" data-mp="14fktd1apwvzu">
                      8314-1666
                    </p>
                    <p className="text-sm text-emerald-800 font-medium" data-id="element-2064" data-mp="1dh22u0tmdzfk">
                      A nombre de {sinpeAccountHolder}
                    </p>
                    <p className="text-xs text-emerald-700 mt-3 leading-relaxed" data-id="element-2065" data-mp="19fyq3n6vcjik">
                      Hacé el SINPE por el monto total del pedido y luego
                      adjuntá la captura del comprobante. Sin la captura no
                      podemos procesar tu pedido.
                    </p>
                  </div>

                  <label className="block text-sm font-bold text-gray-900 mb-2 flex items-center gap-1.5" data-id="element-2066" data-mp="uk0z0jbsy5d7">
                    Captura del pago SINPE
                    <span className="text-red-500" data-id="element-2067" data-mp="20tzhx6d6k2u">*</span>
                    {(errors as any).sinpe_proof && <AlertCircle className="w-4 h-4 text-red-500" data-id="element-2068" data-mp="3u4wfz88hd5ty" />}
                  </label>

                  {!sinpeProofFile ? <label className={cn('flex flex-col items-center justify-center w-full h-40 rounded-xl border-2 border-dashed cursor-pointer transition-all', (errors as any).sinpe_proof ? 'border-red-300 bg-red-50 text-red-600' : 'border-gray-200 bg-gray-50 text-gray-500 hover:border-emerald-300 hover:bg-emerald-50/50 hover:text-emerald-600')} data-id="element-2069" data-mp="3os7ociqopid4">
                      <Upload className="w-8 h-8 mb-2" data-id="element-2070" data-mp="3suukgvue1pg4" />
                      <span className="font-semibold text-sm" data-id="element-2071" data-mp="3swf9y71uii74">
                        Tocá para subir captura
                      </span>
                      <span className="text-xs mt-1" data-id="element-2072" data-mp="2hemkn26ccs85">
                        JPG, PNG o screenshot
                      </span>
                      <input type="file" accept="image/*" className="hidden" onChange={e => {
              const file = e.target.files?.[0];
              if (file) {
                setSinpeProofFile(file);
                setSinpeProofPreview(URL.createObjectURL(file));
                setErrors(prev => ({
                  ...(prev as any),
                  sinpe_proof: undefined
                }));
              }
            }} data-id="element-2073" data-mp="2mfk19abpde7j" />
                    </label> : <div className="relative rounded-xl border-2 border-emerald-200 bg-emerald-50/50 p-3" data-id="element-2074" data-mp="3rcvu02kqcf8z">
                      <img src={sinpeProofPreview} alt="Comprobante SINPE" className="w-full max-h-72 object-contain rounded-lg bg-white" data-id="element-2075" data-mp="1ukdj7bm6jicc" />
                      <div className="flex items-center justify-between mt-3" data-id="element-2076" data-mp="1x40hp22bycvr">
                        <div className="flex items-center gap-2 text-sm text-emerald-700 font-semibold min-w-0" data-id="element-2077" data-mp="223mqmzb4l6yr">
                          <CheckCircle2 className="w-4 h-4 shrink-0" data-id="element-2078" data-mp="26vwf0awldyyx" />
                          <span className="truncate" data-id="element-2079" data-mp="2btoj03aihbjh">
                            {sinpeProofFile.name}
                          </span>
                        </div>
                        <button type="button" onClick={() => {
                setSinpeProofFile(null);
                setSinpeProofPreview('');
              }} className="p-1.5 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors shrink-0" aria-label="Quitar captura" data-id="element-2080" data-mp="1e8ac74v6uyd4">
                          <X className="w-4 h-4" data-id="element-2081" data-mp="34hkckz4sanc1" />
                        </button>
                      </div>
                    </div>}

                  {(errors as any).sinpe_proof && <p className="text-sm text-red-500 mt-2 flex items-center gap-1.5" data-id="element-2082" data-mp="3kow14p0ncuzj">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0" data-id="element-2083" data-mp="384qcjloiqd3" />
                      {(errors as any).sinpe_proof}
                    </p>}

                  {uploadingProof && <p className="text-sm text-emerald-700 mt-2 flex items-center gap-1.5" data-id="element-2084" data-mp="c6q29vwnb74w">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" data-id="element-2085" data-mp="854ma1otecqh" />
                      Subiendo comprobante...
                    </p>}

                  <p className="text-xs text-gray-500 mt-3" data-id="element-2086" data-mp="h6unu0ceus1z">
                    Sin la captura no podemos procesar tu pedido.
                  </p>
                </div>}
            </> : <>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-2087" data-mp="2iz0s35b0cn65">
                <CreditCard className="w-5 h-5 text-brand-primary" data-id="element-2088" data-mp="yfwwpw7sbfex" />
                {t('checkout_payment')}
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-2089" data-mp="13dyiiowlhsu3">
                {restaurant?.accepts_cash && <button type="button" onClick={() => setFormData(prev => ({
            ...prev,
            payment_method: 'cash'
          }))} className={cn('relative flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all', formData.payment_method === 'cash' ? 'border-brand-primary bg-brand-primary/10 text-brand-primary ring-2 ring-brand-primary/20' : 'border-gray-100 hover:border-brand-primary/30 text-gray-600')} data-id="element-2090" data-mp="ooapeayki0hg">
                    {formData.payment_method === 'cash' && <CheckCircle2 className="absolute top-2 right-2 w-5 h-5 text-brand-primary" data-id="element-2091" data-mp="36f3r35avm5cd" />}
                    <Banknote className="w-6 h-6 mb-2" data-id="element-2092" data-mp="3achv8ijp9mxi" />
                    <span className="font-semibold" data-id="element-2093" data-mp="3fd5mn5buc5s9">{t('checkout_cash')}</span>
                  </button>}

                {restaurant?.accepts_card &&
          // Emilius: la tarjeta se cobra con datáfono en el
          // local, así que aplica a Llevar y Comer en el local,
          // pero NO a Delivery (el mensajero no lleva datáfono).
          !(isEmilius && formData.delivery_type === 'delivery') && !isQueGalleta && !(isCrackPollo && formData.delivery_type === 'delivery') && <button type="button" onClick={() => setFormData(prev => ({
            ...prev,
            payment_method: 'card'
          }))} className={cn('relative flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all', formData.payment_method === 'card' ? 'border-brand-primary bg-brand-primary/10 text-brand-primary ring-2 ring-brand-primary/20' : 'border-gray-100 hover:border-brand-primary/30 text-gray-600')} data-id="element-2094" data-mp="23r78q7zytl88">
                      {formData.payment_method === 'card' && <CheckCircle2 className="absolute top-2 right-2 w-5 h-5 text-brand-primary" data-id="element-2095" data-mp="2a5crvsd0eafd" />}
                      <CreditCard className="w-6 h-6 mb-2" data-id="element-2096" data-mp="2675cb0ua7f5r" />
                      <span className="font-semibold" data-id="element-2097" data-mp="ysk2upbls030">
                        {t('checkout_card')}
                      </span>
                    </button>}

                {restaurant?.accepts_sinpe && <button type="button" onClick={() => setFormData(prev => ({
            ...prev,
            payment_method: 'sinpe'
          }))} className={cn('relative flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all', formData.payment_method === 'sinpe' ? 'border-brand-primary bg-brand-primary/10 text-brand-primary ring-2 ring-brand-primary/20' : 'border-gray-100 hover:border-brand-primary/30 text-gray-600')} data-id="element-2098" data-mp="w7m7lj0fcy1z">
                    {formData.payment_method === 'sinpe' && <CheckCircle2 className="absolute top-2 right-2 w-5 h-5 text-brand-primary" data-id="element-2099" data-mp="3qnmwm0benpa0" />}
                    <Smartphone className="w-6 h-6 mb-2" data-id="element-2100" data-mp="3uqsyywvecwb7" />
                    <span className="font-semibold" data-id="element-2101" data-mp="3c1fsksvzrmah">{t('checkout_sinpe')}</span>
                  </button>}
              </div>

              {/* Crack Pollo & Cabaña Parrillera (marketplace): Cash amount input */}
              {(isCrackPollo || isCabanaParrillera && !isQrTableForThisRestaurant) && formData.payment_method === 'cash' && <div className="mt-6 p-5 rounded-2xl border border-brand-amber/30 bg-brand-amber/5" data-id="element-2102" data-mp="3uy8m8q92mp5c">
                    <label className="block text-sm font-bold text-gray-900 mb-2" data-id="element-2103" data-mp="3pz4gyap9pbka">
                      ¿Con cuánto vas a pagar?
                    </label>
                    <div className="relative" data-id="element-2104" data-mp="3kkzg54zxg9l">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium" data-id="element-2105" data-mp="3lw2g2qmajzn">
                        ₡
                      </span>
                      <Input type="number" placeholder="Ej. 10000" value={cashAmount} onChange={e => setCashAmount(e.target.value)} className="pl-8" data-id="element-2106" data-mp="clj3uojcjpsp" />
                    </div>
                    {cashAmount && Number(cashAmount) > 0 && <div className="mt-3 text-sm flex items-center justify-between bg-white p-3 rounded-xl border border-gray-100" data-id="element-2107" data-mp="1aesjxqkhe0wx">
                        <span className="text-gray-600" data-id="element-2108" data-mp="dpkyeijv3rt0">
                          Vuelto a entregar:
                        </span>
                        <span className="font-bold text-gray-900" data-id="element-2109" data-mp="hqekr7hnoyjs">
                          {Number(cashAmount) >= orderTotal ? `₡${(Number(cashAmount) - orderTotal).toLocaleString('es-CR')}` : 'Monto insuficiente'}
                        </span>
                      </div>}
                  </div>}

              {/* SINPE proof upload — for any restaurant accepting SINPE */}
              {formData.payment_method === 'sinpe' && <div className="mt-6" data-field="sinpe_proof" data-id="element-2110" data-mp="3imf9kb9x5kx8">
                  {restaurant?.whatsapp_number ? <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-5 mb-4" data-id="element-2111" data-mp="3dv705f8l9zk2">
                      <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-1" data-id="element-2112" data-mp="38x5jt22jseca">
                        Realizá el SINPE Móvil a este número
                      </p>
                      <p className="text-3xl sm:text-4xl font-black text-emerald-900 tracking-tight tabular-nums mb-1" data-id="element-2113" data-mp="2zw79y35fhcls">
                        {String(restaurant.whatsapp_number).replace(/\D/g, '').replace(/^506/, '').replace(/^(\d{4})(\d{4}).*/, '$1-$2')}
                      </p>
                      {restaurant?.name && <p className="text-sm text-emerald-800 font-medium" data-id="element-2114" data-mp="o5v6iocespgg">
                          A nombre de {sinpeAccountHolder}
                        </p>}
                      <p className="text-xs text-emerald-700 mt-3 leading-relaxed" data-id="element-2115" data-mp="2nd1zojjiu2le">
                        Hacé el SINPE por el monto total del pedido y adjuntá la
                        captura del comprobante. Sin la captura no podemos
                        procesar tu pedido.
                      </p>
                    </div> : <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-4 mb-4" data-id="element-2116" data-mp="31dw64wn0dthf">
                      <p className="text-sm text-emerald-800 leading-relaxed" data-id="element-2117" data-mp="36behetoi0ksl">
                        Hacé el SINPE Móvil al número del restaurante por el
                        monto total del pedido y adjuntá la captura del
                        comprobante. Sin la captura no podemos procesar tu
                        pedido.
                      </p>
                    </div>}

                  <label className="block text-sm font-bold text-gray-900 mb-2 flex items-center gap-1.5" data-id="element-2118" data-mp="1kwqm6kue5s5b">
                    Captura del pago SINPE
                    <span className="text-red-500" data-id="element-2119" data-mp="18h0re562lt6v">*</span>
                    {(errors as any).sinpe_proof && <AlertCircle className="w-4 h-4 text-red-500" data-id="element-2120" data-mp="1nc6x0r197pzo" />}
                  </label>

                  {!sinpeProofFile ? <label className={cn('flex flex-col items-center justify-center w-full h-40 rounded-xl border-2 border-dashed cursor-pointer transition-all', (errors as any).sinpe_proof ? 'border-red-300 bg-red-50 text-red-600' : 'border-gray-200 bg-gray-50 text-gray-500 hover:border-emerald-300 hover:bg-emerald-50/50 hover:text-emerald-600')} data-id="element-2121" data-mp="uoksngk7chbr">
                      <Upload className="w-8 h-8 mb-2" data-id="element-2122" data-mp="701j6xpf1yi3" />
                      <span className="font-semibold text-sm" data-id="element-2123" data-mp="6xfd72i6br1z">
                        Tocá para subir captura
                      </span>
                      <span className="text-xs mt-1" data-id="element-2124" data-mp="3mjmcztiwqcsp">
                        JPG, PNG o screenshot
                      </span>
                      <input type="file" accept="image/*" className="hidden" onChange={e => {
              const file = e.target.files?.[0];
              if (file) {
                setSinpeProofFile(file);
                setSinpeProofPreview(URL.createObjectURL(file));
                setErrors(prev => ({
                  ...(prev as any),
                  sinpe_proof: undefined
                }));
              }
            }} data-id="element-2125" data-mp="3rhxmu497cnax" />
                    </label> : <div className="relative rounded-xl border-2 border-emerald-200 bg-emerald-50/50 p-3" data-id="element-2126" data-mp="s20wxwktmkrk">
                      <img src={sinpeProofPreview} alt="Comprobante SINPE" className="w-full max-h-72 object-contain rounded-lg bg-white" data-id="element-2127" data-mp="27nbd5n4rjo6d" />
                      <div className="flex items-center justify-between mt-3" data-id="element-2128" data-mp="1ynokpqu0hspg">
                        <div className="flex items-center gap-2 text-sm text-emerald-700 font-semibold min-w-0" data-id="element-2129" data-mp="2vdfe1mqkh9to">
                          <CheckCircle2 className="w-4 h-4 shrink-0" data-id="element-2130" data-mp="2rb2a93vdrlzj" />
                          <span className="truncate" data-id="element-2131" data-mp="2mkltb9scpi8f">
                            {sinpeProofFile.name}
                          </span>
                        </div>
                        <button type="button" onClick={() => {
                setSinpeProofFile(null);
                setSinpeProofPreview('');
              }} className="p-1.5 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors shrink-0" aria-label="Quitar captura" data-id="element-2132" data-mp="2hl8zg532bawc">
                          <X className="w-4 h-4" data-id="element-2133" data-mp="3hyfo58kvakdh" />
                        </button>
                      </div>
                    </div>}

                  {(errors as any).sinpe_proof && <p className="text-sm text-red-500 mt-2 flex items-center gap-1.5" data-id="element-2134" data-mp="ehgxugqu1zqk">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0" data-id="element-2135" data-mp="9j5o060jfp8c" />
                      {(errors as any).sinpe_proof}
                    </p>}

                  {uploadingProof && <p className="text-sm text-emerald-700 mt-2 flex items-center gap-1.5" data-id="element-2136" data-mp="3vxyupwy15nno">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" data-id="element-2137" data-mp="x99tyk17812i" />
                      Subiendo comprobante...
                    </p>}
                </div>}
            </>}
        </div>}

      {/* Notes — hidden only for the simplified NatuCafé QR flow */}
      {!useSimplifiedQrCheckout && <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-id="element-2138" data-mp="14ulrerraritn">
          <label className="block text-sm font-bold text-gray-900 mb-2" data-id="element-2139" data-mp="19qasx0mvn9sl">
            {t('checkout_notes')}
          </label>
          <Textarea value={formData.notes} onChange={e => setFormData(prev => ({
        ...prev,
        notes: e.target.value
      }))} placeholder={t('checkout_notes_placeholder')} className="resize-none h-24" data-id="element-2140" data-mp="1jprvh96u17kc" />
        </div>}

      {/* Invoice Request — Hidden from the customer-facing checkout. Other
                                                          admin/system flows that handle invoice requests are unaffected. */}
      {false && Number(restaurant?.id) === 19 && <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-id="element-2141" data-mp="1hr8fx1cw4afm">
          <div className="flex items-start justify-between gap-4 mb-4" data-id="element-2142" data-mp="1hovycpb1o233">
            <div data-id="element-2143" data-mp="1ctp222k3tzo7">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2" data-id="element-2144" data-mp="1cq18j3entd9h">
                <Mail className="w-5 h-5 text-brand-primary" data-id="element-2145" data-mp="1rictbwmx3n8c" />
                ¿Necesitas factura?
              </h3>
              <p className="text-sm text-gray-500 mt-1" data-id="element-2146" data-mp="1mogwixie46er">
                Opcional. Si la necesitas, el restaurante te la enviará por
                correo.
              </p>
            </div>
            <div className="flex gap-2 shrink-0" data-id="element-2147" data-mp="1vkpx4fi3tb2h">
              <button type="button" onClick={() => setFormData(prev => ({
            ...prev,
            wants_invoice: true
          }))} className={cn('px-5 py-2 rounded-xl border-2 font-semibold text-sm transition-all', formData.wants_invoice ? 'border-brand-primary bg-brand-primary/10 text-brand-primary' : 'border-gray-200 text-gray-500 hover:border-gray-300')} data-id="element-2148" data-mp="2ag5tir65pj21">
                Sí
              </button>
              <button type="button" onClick={() => setFormData(prev => ({
            ...prev,
            wants_invoice: false,
            invoice_customer_name: '',
            invoice_legal_id: '',
            invoice_email: ''
          }))} className={cn('px-5 py-2 rounded-xl border-2 font-semibold text-sm transition-all', !formData.wants_invoice ? 'border-brand-primary bg-brand-primary/10 text-brand-primary' : 'border-gray-200 text-gray-500 hover:border-gray-300')} data-id="element-2149" data-mp="2d029fgtadcfj">
                No
              </button>
            </div>
          </div>

          {formData.wants_invoice && <div className="space-y-4 pt-2 border-t border-gray-100" data-id="element-2150" data-mp="1odhkug3yi0va">
              <div data-field="invoice_customer_name" data-id="element-2151" data-mp="1ob54269xpwjt">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2152" data-mp="1kal9nagqx71d">
                  Nombre o razón social <span className="text-red-500" data-id="element-2153" data-mp="1fbi4zr4oh2tx">*</span>
                </label>
                <Input value={formData.invoice_customer_name || ''} onChange={e => handleChange('invoice_customer_name' as any, e.target.value)} error={(errors as any).invoice_customer_name} placeholder="Nombre completo o empresa" required data-id="element-2154" data-mp="1adzzybts77r7" />
              </div>
              <div data-field="invoice_legal_id" data-id="element-2155" data-mp="rn8moz6oax7j">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2156" data-mp="rp1wuoghvzka">
                  Cédula / ID fiscal{' '}
                  <span className="text-gray-400" data-id="element-2157" data-mp="2ybhm57x9a1ep">(opcional)</span>
                </label>
                <Input value={formData.invoice_legal_id || ''} onChange={e => handleChange('invoice_legal_id' as any, e.target.value)} placeholder="1-2345-6789" data-id="element-2158" data-mp="33ac2823u9bv4" />
              </div>
              <div data-field="invoice_email" data-id="element-2159" data-mp="3c8o10reuzfyn">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2160" data-mp="2flsz00znbp9w">
                  Correo para enviar la factura{' '}
                  <span className="text-red-500" data-id="element-2161" data-mp="2khqe63z6apl3">*</span>
                </label>
                <Input type="email" value={formData.invoice_email || ''} onChange={e => handleChange('invoice_email' as any, e.target.value)} error={(errors as any).invoice_email} placeholder="email@ejemplo.com" required data-id="element-2162" data-mp="2ojv5ux629c53" />
              </div>
            </div>}
        </div>}

      {/* Terms and Conditions — hidden for returning customers */}
      {!isReturningCustomer && <div className="flex items-start gap-3 px-2" data-id="element-2163" data-mp="3dx2f9hprkkw0">
          <div className="flex items-center h-5" data-id="element-2164" data-mp="3dynbrcvn2dwi">
            <input id="terms" type="checkbox" checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)} className="w-5 h-5 rounded border-gray-300 text-brand-primary focus:ring-brand-primary" data-id="element-2165" data-mp="38x7p5gdtbzc0" />
          </div>
          <div className="text-sm" data-id="element-2166" data-mp="2u9mmtoori455">
            <label htmlFor="terms" className="font-medium text-gray-900" data-id="element-2167" data-mp="2u6gxq9g4v29f">
              {t('checkout_terms')}
            </label>
            <p className="text-gray-600" data-id="element-2168" data-mp="9i2ntzkm2u9f">{t('checkout_terms')}</p>
          </div>
        </div>}

      <Button type="submit" className="w-full h-14 text-lg font-bold" disabled={isLoading || !termsAccepted} data-id="element-2169" data-mp="5h0ws60uloij">
        {isLoading ? <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" data-id="element-2170" data-mp="d2mkm2fwlffw" />
            {t('checkout_placing')}
          </> : t('checkout_place_order')}
      </Button>

      {/* Terms Modal */}
      {showTermsModal && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setShowTermsModal(false)} data-id="element-2171" data-mp="80dureybuti0">
          <div className="relative w-full max-w-lg max-h-[80vh] bg-white rounded-2xl shadow-2xl flex flex-col" onClick={e => e.stopPropagation()} data-id="element-2172" data-mp="10qdy39d6jyir">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0" data-id="element-2173" data-mp="qzrqa1bpgcam">
              <h2 className="text-lg font-bold text-gray-900" data-id="element-2174" data-mp="1ep3wqtfne698">
                Términos y Condiciones
              </h2>
              <button onClick={() => setShowTermsModal(false)} className="p-2 -mr-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100" data-id="element-2175" data-mp="1jhdu2uj3e8lj">
                <AlertCircle className="w-5 h-5" data-id="element-2176" data-mp="3arpfliyzzahw" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5 text-sm text-gray-600 leading-relaxed space-y-4" data-id="element-2177" data-mp="36qdof90h6h7l">
              <h3 className="font-bold text-gray-900" data-id="element-2178" data-mp="31t52cxc7ht12">
                1. Aceptación de los Términos
              </h3>
              <p data-id="element-2179" data-mp="2cf81vcc9d2q">
                Al realizar un pedido a través de NOIZA, usted acepta estos
                términos y condiciones en su totalidad. Si no está de acuerdo
                con alguna parte, no debe utilizar nuestro servicio.
              </p>
              <h3 className="font-bold text-gray-900" data-id="element-2180" data-mp="3kj3b7fntvq5r">
                2. Servicio de Pedidos
              </h3>
              <p data-id="element-2181" data-mp="2e3j4qebhflv6">
                NOIZA actúa como intermediario tecnológico entre el cliente y el
                restaurante. Los pedidos se procesan y confirman directamente
                con el restaurante a través de WhatsApp.
              </p>
              <h3 className="font-bold text-gray-900" data-id="element-2182" data-mp="1yxn65zjasac5">3. Precios y Pagos</h3>
              <p data-id="element-2183" data-mp="23yudjxhj37ia">
                Los precios mostrados incluyen impuestos. El pago se realiza
                directamente al restaurante según el método seleccionado
                (efectivo, SINPE Móvil o tarjeta). NOIZA no procesa pagos.
              </p>
              <h3 className="font-bold text-gray-900" data-id="element-2184" data-mp="2hqqztkwimn6r">4. Entregas</h3>
              <p data-id="element-2185" data-mp="2mp299uiwevog">
                Los tiempos de entrega son estimados y pueden variar según la
                demanda. El costo de envío se muestra antes de confirmar el
                pedido. El restaurante es responsable de la entrega.
              </p>
              <h3 className="font-bold text-gray-900" data-id="element-2186" data-mp="2wmq7wvjmknbw">5. Cancelaciones</h3>
              <p data-id="element-2187" data-mp="xb5wdf70zvlz">
                Las cancelaciones deben coordinarse directamente con el
                restaurante a través de WhatsApp. Una vez que el pedido está en
                preparación, no se garantiza la cancelación.
              </p>
              <h3 className="font-bold text-gray-900" data-id="element-2188" data-mp="38mynej3akk70">6. Datos Personales</h3>
              <p data-id="element-2189" data-mp="3dgt4deriil2e">
                Recopilamos nombre, teléfono y ubicación únicamente para
                procesar su pedido. No compartimos sus datos con terceros más
                allá del restaurante seleccionado. Su información se maneja
                conforme a la Ley de Protección de Datos de Costa Rica.
              </p>
              <h3 className="font-bold text-gray-900" data-id="element-2190" data-mp="8zr1gceqwsz">7. Responsabilidad</h3>
              <p data-id="element-2191" data-mp="6vq979t2dwx">
                NOIZA no es responsable por la calidad de los alimentos, tiempos
                de entrega o cualquier inconveniente con el restaurante.
                Cualquier reclamo debe dirigirse directamente al
                establecimiento.
              </p>
              <h3 className="font-bold text-gray-900" data-id="element-2192" data-mp="26y4mauzdgir4">8. Modificaciones</h3>
              <p data-id="element-2193" data-mp="2bzbvmyh5xpzw">
                Nos reservamos el derecho de modificar estos términos en
                cualquier momento. Los cambios serán efectivos al publicarse en
                la plataforma.
              </p>
              <p className="text-xs text-gray-400 pt-4 border-t border-gray-100" data-id="element-2194" data-mp="1zfna2h042pug">
                Última actualización: Marzo 2026 · Los Santos, Costa Rica
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 shrink-0" data-id="element-2195" data-mp="28h4oluxvejh1">
              <button onClick={() => {
            setTermsAccepted(true);
            setShowTermsModal(false);
          }} className="w-full py-3 rounded-xl bg-brand-amber text-white font-bold text-sm hover:bg-amber-600 transition-colors" data-id="element-2196" data-mp="1kq8x6yxbtqab">
                Aceptar Términos y Condiciones
              </button>
            </div>
          </div>
        </div>}

      {/* ─── La Cabaña Parrillera — Express fee notice ─── */}
      {showCabanaExpressNotice && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setShowCabanaExpressNotice(false)} data-id="element-2197" data-mp="x168560d4v5s">
          <div className="relative w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()} data-id="element-2198" data-mp="3217napcyegav">
            <div className="px-6 pt-6 pb-4 text-center" style={{
          background: 'linear-gradient(135deg, #FF6B35 0%, #F59E0B 100%)'
        }} data-id="element-2199" data-mp="3buoermpmbjbd">
              <div className="text-4xl mb-1" data-id="element-2200" data-mp="2j5rawom5lp25">🔥 🍔 🛖</div>
              <h3 className="text-white font-black text-lg tracking-tight" data-id="element-2201" data-mp="2o27xhedpfvd4">
                Servicio Express
              </h3>
            </div>
            <div className="px-6 py-5 space-y-3" data-id="element-2202" data-mp="2s29rr2h41mid">
              <p className="text-[15px] text-gray-700 leading-relaxed text-center" data-id="element-2203" data-mp="3bqqy1miyvzf">
                El monto del <strong data-id="element-2204" data-mp="7dc4zqaspgb7">express</strong> no está incluido en el
                precio.
              </p>
              <p className="text-[14px] text-gray-600 leading-relaxed text-center" data-id="element-2205" data-mp="1rv4mg6quzzxl">
                Por favor consulte por el costo del express en{' '}
                <strong data-id="element-2206" data-mp="1j44apfqqb4ni">WhatsApp</strong>.
              </p>
              <p className="text-[14px] font-semibold text-[#FF6B35] text-center pt-1" data-id="element-2207" data-mp="1j2jhbtg5hrr8">
                ¡Muchas gracias!
              </p>
            </div>
            <div className="px-6 pb-6" data-id="element-2208" data-mp="196g5fvvhyer3">
              <button type="button" onClick={() => setShowCabanaExpressNotice(false)} className="w-full py-3 rounded-2xl bg-[#FF6B35] hover:bg-[#FF5722] active:scale-[0.98] text-white font-bold text-sm uppercase tracking-wider transition-all shadow-lg shadow-[#FF6B35]/30" data-id="element-2209" data-mp="197hreve1wlnl">
                Entendido
              </button>
            </div>
          </div>
        </div>}
    </form>;
}